// Muscle Score page (templates/page.muscle-score.json): the one script for every c-ms-* section.
// Each section includes it as an ES module, so the browser runs it once per page.
// GSAP + ScrollTrigger come from layout/theme.liquid (window.gsap). Three.js is a vendor
// file (c-ms-three-lite.js) imported on demand when a 3D scene gets near the viewport.
//
//   1. Motion + environment helpers
//   2. Section contract + shared region state
//   3. Visibility lifecycle
//   4. WebGL helpers
//   5. Cuff model (hero)
//   6. Ring field (signal, score)
//   7. Rep data (more than reps)
//   8. Sections
//   9. Boot + theme editor events

// ---------------------------------------------------------------------------
// 1. Motion + environment helpers
// ---------------------------------------------------------------------------

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isSmallViewport() {
  return window.matchMedia('(max-width: 767px)').matches;
}

function isFinePointer() {
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

let gsapRegistered = false;

/** Returns { gsap, ScrollTrigger } or null when GSAP failed to load. */
function getGsap() {
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  if (!gsap || !ScrollTrigger) return null;
  if (!gsapRegistered) {
    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.config({ ignoreMobileResize: true });
    // The theme sets scroll-behavior: smooth on <html>. refresh() jumps to the top to measure and
    // back; a smooth scroll ignores that jump, so every refresh while scrolled (resize, breakpoint
    // change, fonts) put the pins at the wrong scroll positions. Measure with instant scrolling.
    ScrollTrigger.addEventListener('refreshInit', () => setInstantScroll(true));
    ScrollTrigger.addEventListener('refresh', () => setInstantScroll(false));
    gsapRegistered = true;
  }
  return { gsap, ScrollTrigger };
}

function setInstantScroll(on) {
  const html = document.documentElement;
  if (on) html.style.setProperty('scroll-behavior', 'auto');
  else html.style.removeProperty('scroll-behavior');
  // Flush styles so the very next scrollTo() already uses the new behavior.
  void getComputedStyle(html).scrollBehavior;
}

/** Scrolls without the theme's smooth scroll-behavior. */
function jumpTo(y) {
  setInstantScroll(true);
  window.scrollTo(0, y);
  setInstantScroll(false);
}

/** True when scroll-driven choreography should run (also in the theme editor preview). */
function canChoreograph() {
  return !prefersReducedMotion() && !!getGsap();
}

const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));

/** Maps v from [a, b] to [0, 1], clamped. */
const range = (v, a, b) => clamp((v - a) / (b - a));

const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

/** Frame-rate independent damping toward a target. */
const damp = (current, target, lambda, dt) => current + (target - current) * (1 - Math.exp(-lambda * dt));

const rootVar = (name) => parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || 0;

/**
 * Bars stuck above the header (c-header measures them). On this theme it's 0 at the top of
 * the page and the announcement bar's height once it sticks, depending on the last measure.
 */
const stickyTop = () => rootVar('--c-header-sticky-top');

/** Reserve space for this theme's sticky header and announcement bar. */
function headerOffset() {
  return rootVar('--header-height') + stickyTop();
}

/**
 * Pinned scenes run at every width (phones get the same choreography as desktop);
 * each section then checks that its content fits the pinned frame (see measureLive).
 */
function canPin() {
  return canChoreograph();
}

/**
 * Runs measure() with the section in its pinned ("live") layout, then restores its current mode.
 * Pinned frames are sized with the sticky-top offset, which changes with scroll position; measure()
 * adds stickyTop() back so the pin/no-pin choice is the same wherever the page was scrolled.
 */
function measureLive(root, measure) {
  const mode = root.dataset.cMsMode;
  root.dataset.cMsMode = 'live';
  const result = measure();
  if (mode === undefined) delete root.dataset.cMsMode;
  else root.dataset.cMsMode = mode;
  return result;
}

// ---------------------------------------------------------------------------
// 2. Section contract + shared region state
// Every section exposes init(root) / destroy(root), matching Shopify's
// shopify:section:load / shopify:section:unload events.
// ---------------------------------------------------------------------------

/**
 * @param {(root: HTMLElement, settings: Record<string, any>) => (void | (() => void))} setup
 *   Runs once per root; returns a cleanup function.
 */
function defineSection(setup) {
  const instances = new WeakMap();
  return {
    init(root) {
      if (instances.has(root)) return;
      const cleanup = setup(root, readSettings(root)) || (() => {});
      instances.set(root, cleanup);
    },
    destroy(root) {
      const cleanup = instances.get(root);
      if (!cleanup) return;
      cleanup();
      instances.delete(root);
    },
  };
}

/** Reads the JSON settings a section template embeds for its JS. */
function readSettings(root) {
  const node = root.querySelector(':scope > script[type="application/json"][data-c-ms-settings]');
  if (!node) return {};
  try {
    return JSON.parse(node.textContent);
  } catch {
    return {};
  }
}

// The body region chosen in "How it works", shared with "Score".
let region = 'lower';
const REGION_EVENT = 'c-ms:region-change';

function getRegion() {
  return region;
}

function setRegion(next) {
  if (next === region) return;
  region = next;
  document.dispatchEvent(new CustomEvent(REGION_EVENT, { detail: { region } }));
}

function onRegionChange(fn) {
  const handler = (e) => fn(e.detail.region);
  document.addEventListener(REGION_EVENT, handler);
  return () => document.removeEventListener(REGION_EVENT, handler);
}

// ---------------------------------------------------------------------------
// 3. Visibility lifecycle
// Heavy scenes are created only when their section gets near the viewport,
// run only while visible and the tab is visible, and are disposed once far away.
// ---------------------------------------------------------------------------

/**
 * @param {Element} el
 * @param {{ create: () => any, start: () => void, stop: () => void, dispose: () => void }} hooks
 * @param {{ near?: string, far?: string }} [opts]
 * @returns {() => void} cleanup
 */
function lifecycle(el, hooks, opts = {}) {
  const near = opts.near ?? '100% 0px';
  const far = opts.far ?? '250% 0px';
  let created = false; // create() has been called
  let ready = false; // create() has resolved: only then can the scene start
  let visible = false;
  let running = false;
  let generation = 0; // guards against a stale create() resolving after dispose + re-create

  const sync = () => {
    const shouldRun = ready && visible && !document.hidden;
    if (shouldRun && !running) {
      running = true;
      hooks.start();
    } else if (!shouldRun && running) {
      running = false;
      hooks.stop();
    }
  };

  const nearIO = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting) && !created) {
        created = true;
        const mine = ++generation;
        Promise.resolve(hooks.create()).then(
          () => {
            if (!created || mine !== generation) return; // disposed while loading
            ready = true;
            sync();
          },
          (err) => {
            created = false;
            console.warn('[ms] scene failed to start', err);
          }
        );
      }
    },
    { rootMargin: near }
  );

  const farIO = new IntersectionObserver(
    (entries) => {
      if (!entries.some((e) => e.isIntersecting) && created) {
        if (running) {
          running = false;
          hooks.stop();
        }
        created = ready = false;
        generation++;
        hooks.dispose();
      }
    },
    { rootMargin: far }
  );

  const viewIO = new IntersectionObserver(
    (entries) => {
      visible = entries.some((e) => e.isIntersecting);
      sync();
    },
    { rootMargin: '0px' }
  );

  nearIO.observe(el);
  farIO.observe(el);
  viewIO.observe(el);
  document.addEventListener('visibilitychange', sync);

  return () => {
    nearIO.disconnect();
    farIO.disconnect();
    viewIO.disconnect();
    document.removeEventListener('visibilitychange', sync);
    if (running) hooks.stop();
    if (created) hooks.dispose();
    created = ready = running = false;
  };
}

/** Toggles a class while the element is on screen (used to pause CSS loops and play reveals). */
function inViewClass(el, className = 'is-inview', rootMargin = '0px') {
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) e.target.classList.toggle(className, e.isIntersecting);
    },
    { rootMargin }
  );
  io.observe(el);
  return () => {
    io.disconnect();
    el.classList.remove(className);
  };
}

// ---------------------------------------------------------------------------
// 4. WebGL helpers: capability check, lazy Three.js import, and a small scene
// controller that plugs into lifecycle() (create/start/stop/dispose).
// ---------------------------------------------------------------------------

let threePromise = null;

/** Lazily imports the Three.js slice. Sections pass its versioned asset URL in data-c-ms-three. */
function loadThree() {
  if (!threePromise) {
    const src =
      document.querySelector('[data-c-ms-three]')?.dataset.cMsThree ||
      new URL('c-ms-three-lite.js', import.meta.url).href;
    threePromise = import(src).catch((error) => {
      threePromise = null;
      throw error;
    });
  }
  return threePromise;
}

/** Runs fn once the page has loaded and the main thread is idle. */
function whenIdle(fn, timeout = 2000) {
  let cancelled = false;
  let handle = 0;
  const run = () => {
    if (cancelled) return;
    handle = window.requestIdleCallback
      ? requestIdleCallback(() => !cancelled && fn(), { timeout })
      : setTimeout(() => !cancelled && fn(), 200);
  };
  if (document.readyState === 'complete') run();
  else window.addEventListener('load', run, { once: true });
  return () => {
    cancelled = true;
    window.removeEventListener('load', run);
    if (window.cancelIdleCallback && handle) cancelIdleCallback(handle);
    else clearTimeout(handle);
  };
}

const SOFTWARE_RENDERER = /swiftshader|llvmpipe|softpipe|software|basic render/i;

let webglSupport = null;
/**
 * True when a hardware-accelerated WebGL context is available. Software
 * renderers (no GPU: some VMs, blocklisted drivers, lab tools like Lighthouse)
 * get the static fallbacks instead, since animated 3D would stutter there.
 * Add ?c-ms-gl=force to the URL to bypass the software check (QA screenshots).
 */
function hasWebGL() {
  if (webglSupport !== null) return webglSupport;
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    if (!gl) {
      webglSupport = false;
    } else {
      const info = gl.getExtension('WEBGL_debug_renderer_info');
      const name = String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER));
      const force = new URLSearchParams(location.search).get('c-ms-gl') === 'force';
      webglSupport = force || !SOFTWARE_RENDERER.test(name);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    }
  } catch {
    webglSupport = false;
  }
  return webglSupport;
}

function pixelRatio() {
  return Math.min(window.devicePixelRatio || 1, isSmallViewport() ? 1.5 : 2);
}

/** Particle budget scaled by viewport and device class. */
function particleBudget(desktop, mobile) {
  const cores = navigator.hardwareConcurrency || 4;
  const base = isSmallViewport() ? mobile : desktop;
  return Math.round(cores <= 4 ? base * 0.7 : base);
}

/** Camera distance at which a world-space box (w × h) fits the view. */
function fitDistance(camera, w, h) {
  const t = 2 * Math.tan((camera.fov * Math.PI) / 360);
  return Math.max(h / t, w / (t * camera.aspect));
}

/** Visible world height at a given camera distance. */
function viewHeight(camera, distance) {
  return 2 * Math.tan((camera.fov * Math.PI) / 360) * distance;
}

/**
 * @param {HTMLElement} container  Element the canvas fills.
 * @param {(THREE: any, ctx: { renderer: any, scene: any, camera: any, canvas: HTMLCanvasElement, width: number, height: number }) =>
 *   Promise<SceneApi> | SceneApi} build
 * @param {{ fov?: number, antialias?: boolean, staticFrame?: boolean, onReady?: () => void }} [opts]
 *
 * SceneApi: { update(dt, time), resize?(w, h), degrade?(), dispose?() }
 */
function glScene(container, build, opts = {}) {
  const fov = opts.fov ?? 30;
  let gen = 0;
  let state = null;
  let raf = 0;
  let last = 0;
  let time = 0;
  let slowFrames = 0;
  let degraded = false;

  const renderOnce = (dt = 0) => {
    if (!state) return;
    state.api.update(dt, time, state.ctx);
    state.renderer.render(state.scene, state.camera);
  };

  const frame = (now) => {
    raf = requestAnimationFrame(frame);
    const dt = Math.min((now - last) / 1000, 0.1);
    last = now;
    time += dt;
    if (!degraded && state?.api.degrade) {
      slowFrames = dt > 0.024 ? slowFrames + 1 : Math.max(0, slowFrames - 1);
      if (slowFrames > 30) {
        degraded = true;
        state.api.degrade();
      }
    }
    renderOnce(dt);
  };

  const hooks = {
    async create() {
      const my = ++gen;
      const THREE = await loadThree();
      if (my !== gen) return;

      const canvas = document.createElement('canvas');
      canvas.className = 'c-ms-canvas';
      canvas.setAttribute('aria-hidden', 'true');
      const renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: !!opts.antialias,
        alpha: true,
        powerPreference: 'high-performance',
      });
      renderer.setPixelRatio(pixelRatio());
      renderer.setClearColor(0x000000, 0);
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(fov, 1, 0.1, 200);
      const ctx = { renderer, scene, camera, canvas, width: 1, height: 1, THREE };

      let api;
      try {
        api = await build(THREE, ctx);
      } catch (error) {
        renderer.dispose();
        renderer.forceContextLoss?.();
        throw error;
      }
      if (my !== gen) {
        api.dispose?.();
        renderer.dispose();
        return;
      }
      container.appendChild(canvas);

      const resize = () => {
        const w = container.clientWidth;
        const h = container.clientHeight;
        if (!w || !h) return;
        ctx.width = w;
        ctx.height = h;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        api.resize?.(w, h, ctx);
        if (!raf) renderOnce();
      };
      const ro = new ResizeObserver(resize);
      ro.observe(container);

      state = { THREE, renderer, scene, camera, canvas, api, ro, ctx };
      resize();
      renderOnce();
      container.dataset.cMsGl = 'ready';
      opts.onReady?.();
    },
    start() {
      if (!state) return;
      if (opts.staticFrame) {
        renderOnce();
        return;
      }
      if (raf) return;
      last = performance.now();
      raf = requestAnimationFrame(frame);
    },
    stop() {
      cancelAnimationFrame(raf);
      raf = 0;
    },
    dispose() {
      gen++;
      hooks.stop();
      if (!state) return;
      state.ro.disconnect();
      state.api.dispose?.();
      state.renderer.dispose();
      state.renderer.forceContextLoss?.();
      state.canvas.remove();
      state = null;
      delete container.dataset.cMsGl;
    },
    /** Re-render one frame (used by static / reduced-motion scenes). */
    invalidate() {
      if (!raf) renderOnce();
    },
  };
  return hooks;
}

// ---------------------------------------------------------------------------
// 5. Cuff model (hero)
// Solid, lit model of the Suji cuff, built procedurally until a production GLB
// exists: a black knit band with red piping on both edges, an overlapping strap
// with a rounded end that carries the printed SUJI wordmark and mark, a gray inner
// lining, and a thick glossy shield-shaped sensor pod on a satin base.
// Units: band radius 1. The cuff's axis is Y, and theta = 0 faces +Z (the camera).
// theta increases to the right when seen from outside.
// ---------------------------------------------------------------------------

const buildCuff = (() => {
  const R = 1;
  const H = 0.86;
  const STRAP_H = H * 0.96;
  const STRAP_END = 0.42; // the visible, finished end of the strap
  const STRAP_START = STRAP_END - 2.4;
  const STRAP_CORNER = 0.32; // near-semicircular end, as in the renders
  const POD_THETA = 0.94;
  const POD_W = 0.29; // half width
  const POD_H = 0.28; // half height
  const POD_T = 0.19; // shell thickness
  // The strap print (wordmark + mark) covers this arc, ending just short of the rounded end.
  const PRINT_END = STRAP_END - 0.1;
  const PRINT_ARC = 1.3;

  /** Fine horizontal knit, used as a bump map. */
  function knitTexture(THREE) {
    const size = 128;
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const g = c.getContext('2d');
    g.fillStyle = '#808080';
    g.fillRect(0, 0, size, size);
    for (let y = 0; y < size; y += 4) {
      g.fillStyle = 'rgba(255,255,255,0.28)';
      g.fillRect(0, y, size, 1);
      g.fillStyle = 'rgba(0,0,0,0.3)';
      g.fillRect(0, y + 2, size, 1);
    }
    const img = g.getImageData(0, 0, size, size);
    for (let i = 0; i < img.data.length; i += 4) {
      const n = (Math.random() - 0.5) * 36;
      img.data[i] += n;
      img.data[i + 1] += n;
      img.data[i + 2] += n;
    }
    g.putImageData(img, 0, 0);
    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    return tex;
  }

  /** Glossy black studio matcap for the pod: soft top-left key, faint red rim (cheap stand-in for env reflections). */
  function glossMatcap(THREE) {
    const size = 256;
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const g = c.getContext('2d');
    const m = size / 2;
    let grad = g.createRadialGradient(m, m, 0, m, m, m);
    grad.addColorStop(0, '#1c1c1e');
    grad.addColorStop(0.7, '#0b0b0c');
    grad.addColorStop(1, '#020202');
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
    // red rim, lower right
    grad = g.createRadialGradient(m * 1.55, m * 1.5, 0, m * 1.55, m * 1.5, m * 0.9);
    grad.addColorStop(0, 'rgba(225,0,23,0.22)');
    grad.addColorStop(1, 'rgba(225,0,23,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
    // broad soft key
    grad = g.createRadialGradient(m * 0.72, m * 0.58, 0, m * 0.72, m * 0.58, m * 0.6);
    grad.addColorStop(0, 'rgba(255,255,255,0.32)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
    // sharp specular
    grad = g.createRadialGradient(m * 0.64, m * 0.5, 0, m * 0.64, m * 0.5, m * 0.13);
    grad.addColorStop(0, 'rgba(255,255,255,0.95)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  /**
   * The Suji mark: a small lobe joined to a large lobe by a thin neck.
   * Drawn as a smooth union of two circles (signed distance), horizontal, small lobe left.
   * `size` is the big lobe's radius in pixels.
   */
  function drawMark(g, cx, cy, size, color = '#f2f1ef') {
    const rs = 0.46;
    const D = 2.25;
    const k = 0.59;
    const xb = D / 2 - 0.4; // centers the whole shape on cx
    const xs = -D / 2 - 0.4;
    const w = Math.ceil(size * 4);
    const h = Math.ceil(size * 2.4);
    const tmp = document.createElement('canvas');
    tmp.width = w;
    tmp.height = h;
    const t = tmp.getContext('2d');
    const img = t.createImageData(w, h);
    const [r, gg, b] = [1, 3, 5].map((i) => parseInt(color.slice(i, i + 2), 16));
    const aa = 1.2 / size; // ~1px anti-alias band
    for (let py = 0; py < h; py++) {
      for (let px = 0; px < w; px++) {
        const x = (px + 0.5 - w / 2) / size;
        const y = (py + 0.5 - h / 2) / size;
        const d1 = Math.hypot(x - xs, y) - rs;
        const d2 = Math.hypot(x - xb, y) - 1;
        const d = -k * Math.log(Math.exp(-d1 / k) + Math.exp(-d2 / k));
        const a = Math.min(1, Math.max(0, 0.5 - d / aa));
        if (a > 0) {
          const o = (py * w + px) * 4;
          img.data[o] = r;
          img.data[o + 1] = gg;
          img.data[o + 2] = b;
          img.data[o + 3] = a * 255;
        }
      }
    }
    t.putImageData(img, 0, 0);
    g.drawImage(tmp, cx - w / 2, cy - h / 2);
  }

  /**
   * The SUJI wordmark as it's printed on the strap: light, geometric letters with
   * stencil breaks. Hand-drawn stand-in; swap in the official vector when available.
   * (x, y) is the top-left corner, `h` the cap height in pixels. Returns the width drawn.
   */
  function drawWordmark(g, x, y, h, color = '#f2f1ef') {
    const sw = h * 0.15;
    const u = (v) => v * h;
    const lw = 0.56; // letter width, in cap heights
    const gap = 0.3;
    g.save();
    g.strokeStyle = color;
    g.lineWidth = sw;
    g.lineCap = 'round';
    g.lineJoin = 'round';
    const top = sw / 2 / h;
    const bot = 1 - top;
    const rr = 0.2; // corner radius
    const letters = [
      // S: top bar into the upper-left corner, break, middle bar into the lower-right corner and bottom bar.
      (ox) => {
        g.beginPath();
        g.moveTo(u(ox + lw), u(y / h + top));
        g.arcTo(u(ox + top), u(y / h + top), u(ox + top), u(y / h + 0.4), u(rr));
        g.lineTo(u(ox + top), u(y / h + 0.33));
        g.moveTo(u(ox + 0.14), u(y / h + 0.5));
        g.arcTo(u(ox + lw - top), u(y / h + 0.5), u(ox + lw - top), u(y / h + bot), u(rr));
        g.arcTo(u(ox + lw - top), u(y / h + bot), u(ox), u(y / h + bot), u(rr));
        g.lineTo(u(ox + top), u(y / h + bot));
        g.stroke();
      },
      // U: left stem with a break before the bowl, bowl into the right stem.
      (ox) => {
        g.beginPath();
        g.moveTo(u(ox + top), u(y / h + top));
        g.lineTo(u(ox + top), u(y / h + 0.6));
        g.moveTo(u(ox + 0.16), u(y / h + bot));
        g.arcTo(u(ox + lw - top), u(y / h + bot), u(ox + lw - top), u(y / h + top), u(rr));
        g.lineTo(u(ox + lw - top), u(y / h + top));
        g.stroke();
      },
      // J: stem with a break before the hook.
      (ox) => {
        g.beginPath();
        g.moveTo(u(ox + lw - top), u(y / h + top));
        g.lineTo(u(ox + lw - top), u(y / h + 0.6));
        g.moveTo(u(ox + lw - 0.16), u(y / h + bot));
        g.arcTo(u(ox + top), u(y / h + bot), u(ox + top), u(y / h + 0.6), u(rr));
        g.lineTo(u(ox + top), u(y / h + 0.72));
        g.stroke();
      },
      // I
      (ox) => {
        g.beginPath();
        g.moveTo(u(ox + top), u(y / h + top));
        g.lineTo(u(ox + top), u(y / h + bot));
        g.stroke();
      },
    ];
    let ox = x / h;
    letters.forEach((draw, i) => {
      draw(ox);
      ox += (i === 3 ? 0 : lw) + gap;
    });
    g.restore();
    return (ox - x / h) * h + sw;
  }

  /** Transparent print for the strap: wordmark, then the mark toward the strap end. */
  function strapPrintTexture(THREE, arcLen, height) {
    const W = 1536;
    const Hpx = Math.round((W * height) / arcLen);
    const c = document.createElement('canvas');
    c.width = W;
    c.height = Hpx;
    const g = c.getContext('2d');
    const pxPerUnit = W / arcLen;
    const cap = 0.19 * pxPerUnit;
    drawWordmark(g, 0.1 * pxPerUnit, Hpx / 2 - cap / 2, cap);
    drawMark(g, W - 0.3 * pxPerUnit, Hpx / 2, 0.105 * pxPerUnit);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    return tex;
  }

  /** Small square texture with the mark centered, for the pod window. */
  function markTexture(THREE) {
    const c = document.createElement('canvas');
    c.width = 256;
    c.height = 128;
    drawMark(c.getContext('2d'), 128, 64, 36);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  /** Point on the band surface at angle theta, height y, radius r. */
  const onBand = (THREE, theta, y, r) => new THREE.Vector3(r * Math.sin(theta), y, r * Math.cos(theta));

  /** Places a flat part tangent to the band, facing outward. */
  function mount(obj, theta, y, r) {
    obj.position.set(r * Math.sin(theta), y, r * Math.cos(theta));
    obj.rotation.y = theta;
    return obj;
  }

  /** Keeps a strap point (theta, y) inside the rounded end: corners are arcs of radius STRAP_CORNER. */
  function roundEnd(theta, y) {
    const top = STRAP_H / 2;
    const c = STRAP_CORNER;
    const s0 = STRAP_END - c;
    const y0 = Math.sign(y) * (top - c);
    if (theta <= s0 || Math.abs(y) <= top - c) return [Math.min(theta, STRAP_END), y];
    const dx = theta - s0;
    const dy = y - y0;
    const len = Math.hypot(dx, dy);
    if (len <= c) return [theta, y];
    return [s0 + (dx / len) * c, y0 + (dy / len) * c];
  }

  /** The overlapping strap: a curved sheet with a rounded end, as a subdivided grid wrapped onto the band. */
  function strapGeometry(THREE, r) {
    const geo = new THREE.PlaneGeometry(STRAP_END - STRAP_START, STRAP_H, 220, 24);
    const p = geo.attributes.position;
    for (let i = 0; i < p.count; i++) {
      const theta = STRAP_START + (p.getX(i) + (STRAP_END - STRAP_START) / 2);
      const [t, y] = roundEnd(theta, p.getY(i));
      p.setXYZ(i, r * Math.sin(t), y, r * Math.cos(t));
    }
    geo.computeVertexNormals();
    return geo;
  }

  /** Outline of the strap: top edge, rounded end, bottom edge. */
  function strapOutline(THREE, r) {
    const pts = [];
    const top = STRAP_H / 2 - 0.004;
    const c = STRAP_CORNER;
    const s0 = STRAP_END - c;
    const steps = 60;
    for (let i = 0; i <= steps; i++) pts.push(onBand(THREE, STRAP_START + (s0 - STRAP_START) * (i / steps), top, r));
    for (let i = 1; i <= 16; i++) {
      const a = (i / 16) * (Math.PI / 2);
      pts.push(onBand(THREE, s0 + Math.sin(a) * c, top - c + Math.cos(a) * c, r));
    }
    const straight = 2 * (top - c);
    const n = Math.max(1, Math.round(straight / 0.04));
    for (let i = 1; i < n; i++) pts.push(onBand(THREE, STRAP_END, top - c - straight * (i / n), r));
    for (let i = 0; i <= 16; i++) {
      const a = (i / 16) * (Math.PI / 2);
      pts.push(onBand(THREE, s0 + Math.cos(a) * c, -top + c - Math.sin(a) * c, r));
    }
    for (let i = 1; i <= steps; i++) pts.push(onBand(THREE, s0 - (s0 - STRAP_START) * (i / steps), -top, r));
    return new THREE.CatmullRomCurve3(pts, false, 'centripetal');
  }

  /**
   * Shield-shaped pod: flat, wide top edge, sides tapering to a rounded point at the bottom,
   * a nearly flat glossy face and steep rounded sides. Built by squaring off a sphere.
   * Local frame: face toward +Z, back at z = 0.
   */
  function podGeometry(THREE, thickness) {
    const geo = new THREE.SphereGeometry(1, 72, 56);
    const p = geo.attributes.position;
    const sq = (v, e) => Math.sign(v) * Math.pow(Math.abs(v), e);
    for (let i = 0; i < p.count; i++) {
      let x = sq(p.getX(i), 0.62);
      let y = sq(p.getY(i), 0.62);
      const z0 = p.getZ(i);
      const z = z0 > 0 ? sq(z0, 0.4) : z0 * 0.02;
      // Taper toward the bottom, and round the bottom into a soft point.
      x *= 1 - 0.42 * Math.pow(Math.max(0, -y), 1.2);
      y -= 0.08 * Math.max(0, -y) * (1 - Math.abs(x));
      p.setXYZ(i, x * POD_W, y * POD_H, z * thickness);
    }
    geo.computeVertexNormals();
    return geo;
  }

  /** Rounded, downward-pointing shield outline for the pod's mark window. */
  function shieldShape(THREE, w, h) {
    const s = new THREE.Shape();
    const r = w * 0.18;
    s.moveTo(-w / 2 + r, h / 2);
    s.lineTo(w / 2 - r, h / 2);
    s.quadraticCurveTo(w / 2, h / 2, w / 2, h / 2 - r);
    s.quadraticCurveTo(w / 2, -h * 0.05, r * 0.6, -h / 2 + r * 0.3);
    s.quadraticCurveTo(0, -h / 2 - r * 0.2, -r * 0.6, -h / 2 + r * 0.3);
    s.quadraticCurveTo(-w / 2, -h * 0.05, -w / 2, h / 2 - r);
    s.quadraticCurveTo(-w / 2, h / 2, -w / 2 + r, h / 2);
    return s;
  }

  /**
   * @returns {{ object: any, setPressure: (b: number) => void, dispose: () => void }}
   */
  return function buildCuff(THREE) {
    const knit = knitTexture(THREE);
    const knitBand = knit.clone();
    knitBand.repeat.set(28, 3);
    knitBand.needsUpdate = true;

    const matcap = glossMatcap(THREE);
    const strapR = R + 0.016;
    const print = strapPrintTexture(THREE, PRINT_ARC * strapR, STRAP_H);
    const podMarkTex = markTexture(THREE);

    // Standard (not physical) materials keep shader compile time low on phones.
    const mats = {
      fabric: new THREE.MeshStandardMaterial({ color: 0x1d1d20, roughness: 0.82, bumpMap: knitBand, bumpScale: 3 }),
      strap: new THREE.MeshStandardMaterial({
        color: 0x1f1f22,
        roughness: 0.8,
        bumpMap: knitBand,
        bumpScale: 3,
        side: THREE.DoubleSide,
      }),
      lining: new THREE.MeshStandardMaterial({
        color: 0x45454b,
        roughness: 0.95,
        side: THREE.BackSide,
        bumpMap: knitBand,
        bumpScale: 1.2,
      }),
      piping: new THREE.MeshStandardMaterial({
        color: 0x8c0004,
        roughness: 0.6,
        emissive: new THREE.Color(0xe10017),
        emissiveIntensity: 0,
      }),
      // Printed ink: lit like the fabric, slightly brighter so it reads in the dark hero.
      print: new THREE.MeshStandardMaterial({
        map: print,
        transparent: true,
        alphaTest: 0.02,
        roughness: 0.7,
        emissive: new THREE.Color(0xf2f1ef),
        emissiveMap: print,
        emissiveIntensity: 0.18,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        depthWrite: false,
      }),
      gloss: new THREE.MeshMatcapMaterial({ matcap }),
      podBase: new THREE.MeshStandardMaterial({ color: 0x1a1a1c, roughness: 0.42, metalness: 0.2 }),
      window: new THREE.MeshStandardMaterial({ color: 0x3a3a40, roughness: 0.35, metalness: 0.1 }),
      podMark: new THREE.MeshBasicMaterial({ map: podMarkTex, transparent: true, depthWrite: false }),
    };

    const cuff = new THREE.Group();

    // Band + lining
    cuff.add(new THREE.Mesh(new THREE.CylinderGeometry(R, R, H, 180, 1, true), mats.fabric));
    cuff.add(new THREE.Mesh(new THREE.CylinderGeometry(R - 0.03, R - 0.03, H, 180, 1, true), mats.lining));

    // Piping on both edges
    const ring = new THREE.TorusGeometry(R - 0.012, 0.026, 18, 260);
    for (const y of [H / 2, -H / 2]) {
      const t = new THREE.Mesh(ring, mats.piping);
      t.rotation.x = Math.PI / 2;
      t.position.y = y;
      cuff.add(t);
    }

    // Overlapping strap with a rounded end and its own piped edge
    cuff.add(new THREE.Mesh(strapGeometry(THREE, strapR), mats.strap));
    cuff.add(new THREE.Mesh(new THREE.TubeGeometry(strapOutline(THREE, strapR + 0.006), 480, 0.021, 12, false), mats.piping));

    // Printed SUJI wordmark + mark on the strap
    const printGeo = new THREE.CylinderGeometry(strapR + 0.002, strapR + 0.002, STRAP_H, 96, 1, true, PRINT_END - PRINT_ARC, PRINT_ARC);
    cuff.add(new THREE.Mesh(printGeo, mats.print));

    // Sensor pod: satin base, glossy shell, and a small window carrying the mark
    const pod = new THREE.Group();
    const base = new THREE.Mesh(podGeometry(THREE, 0.05), mats.podBase);
    base.scale.set(1.035, 1.035, 1);
    const shell = new THREE.Mesh(podGeometry(THREE, POD_T), mats.gloss);
    shell.position.z = 0.03;
    pod.add(base, shell);

    // Find the face surface below center (highest shell vertex there), and seat the window + mark on it.
    const sp = shell.geometry.attributes.position;
    let faceZ = 0;
    for (let i = 0; i < sp.count; i++) {
      if (Math.abs(sp.getX(i)) < 0.04 && Math.abs(sp.getY(i) + 0.12) < 0.05) faceZ = Math.max(faceZ, sp.getZ(i));
    }
    faceZ += shell.position.z;
    const win = new THREE.Mesh(new THREE.ShapeGeometry(shieldShape(THREE, 0.15, 0.12), 12), mats.window);
    win.position.set(0, -0.12, faceZ + 0.003);
    const podMark = new THREE.Mesh(new THREE.PlaneGeometry(0.1, 0.05), mats.podMark);
    podMark.position.set(0, -0.11, faceZ + 0.005);
    pod.add(win, podMark);
    cuff.add(mount(pod, POD_THETA, 0.01, R + 0.004));

    return {
      object: cuff,
      /** b in 0..1: inflation pressure, drives a subtle glow in the piping. */
      setPressure(b) {
        mats.piping.emissiveIntensity = 0.18 * b;
      },
      dispose() {
        cuff.traverse((o) => o.geometry?.dispose());
        Object.values(mats).forEach((m) => m.dispose());
        [matcap, knit, knitBand, print, podMarkTex].forEach((t) => t.dispose());
      },
    };
  };
})();

// ---------------------------------------------------------------------------
// 6. Ring field (signal, score)
// One particle system that can be the cuff, the signal and the score. Every
// particle stores its place on the cuff in cylindrical coordinates. The vertex
// shader derives the other two states from that:
//   uMorphA 0 -> 1 : the cuff unrolls into a flat strip, then collapses into two
//                    signal traces (top piping -> pressure, bottom -> movement)
//   uMorphB 0 -> 1 : the traces curl back into a face-on ring (the score dial)
// All animation happens on the GPU, so there's no per-frame CPU work.
// ---------------------------------------------------------------------------

const KIND = { SHELL: 0, LINING: 1, RIM: 2, POD: 3, LOGO_EDGE: 4, LOGO_MARK: 5, POD_EDGE: 6 };

const RING = {
  radius: 1,
  height: 0.72,
  podTheta: 0.42,
  logoTheta: -1.22,
};

function mulberry32(seed) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Procedural cuff: returns Float32Arrays (cyl: theta, r, y, kind; seed: 4 randoms). */
function buildCuffSamples(count, seed = 7) {
  const rand = mulberry32(seed);
  const gauss = () => (rand() + rand() + rand() - 1.5) / 1.5;
  const { radius: R, height: H, podTheta, logoTheta } = RING;
  const cyl = new Float32Array(count * 4);
  const seeds = new Float32Array(count * 4);

  const split = [
    [KIND.SHELL, 0.4],
    [KIND.LINING, 0.1],
    [KIND.RIM, 0.3],
    [KIND.POD, 0.13],
    [KIND.LOGO_EDGE, 0.045],
    [KIND.LOGO_MARK, 0.025],
  ];

  let i = 0;
  const push = (theta, r, y, kind) => {
    // wrap theta to (-PI, PI]
    let t = theta;
    while (t > Math.PI) t -= Math.PI * 2;
    while (t <= -Math.PI) t += Math.PI * 2;
    cyl[i * 4] = t;
    cyl[i * 4 + 1] = r;
    cyl[i * 4 + 2] = y;
    cyl[i * 4 + 3] = kind;
    seeds[i * 4] = rand();
    seeds[i * 4 + 1] = rand();
    seeds[i * 4 + 2] = rand();
    seeds[i * 4 + 3] = rand();
    i++;
  };

  for (const [kind, share] of split) {
    const n = kind === KIND.LOGO_MARK ? count - i : Math.round(count * share);
    for (let k = 0; k < n && i < count; k++) {
      switch (kind) {
        case KIND.SHELL: {
          // Knit-like texture: faint horizontal courses.
          const y = (rand() - 0.5) * (H - 0.04);
          const course = Math.round(y / 0.045) * 0.045;
          const yy = rand() < 0.35 ? course + gauss() * 0.004 : y;
          push(rand() * Math.PI * 2, R + gauss() * 0.004, yy, kind);
          break;
        }
        case KIND.LINING:
          push(rand() * Math.PI * 2, R - 0.035, (rand() - 0.5) * H, kind);
          break;
        case KIND.RIM: {
          // Piping: a thin tube wrapped over each edge.
          const top = k % 2 === 0 ? 1 : -1;
          const psi = rand() * Math.PI * 2;
          const tube = 0.022;
          push(
            rand() * Math.PI * 2,
            R - 0.012 + Math.cos(psi) * tube,
            top * (H / 2) + Math.sin(psi) * tube,
            kind
          );
          break;
        }
        case KIND.POD: {
          // Rounded-triangle pod, domed well out of the band (see renders).
          const a = 0.24; // half width (arc length)
          const b = 0.25; // half height
          let u, v, d;
          const edge = rand() < 0.3;
          for (;;) {
            u = rand() * 2 - 1;
            v = rand() * 2 - 1;
            const narrow = 1 - 0.42 * Math.max(0, -v);
            d = Math.pow(Math.abs(u / narrow), 3) + Math.pow(Math.abs(v), 3);
            if (d <= 1 && (!edge || d > 0.75)) break;
          }
          const bulge = 0.16 * Math.pow(Math.max(0, 1 - d), 0.45);
          push(podTheta + (u * a) / R, R + 0.012 + bulge, v * b + 0.02, edge ? KIND.POD_EDGE : kind);
          // Small light mark on the pod face.
          if (k % 40 === 0 && i < count) {
            const ang = rand() * Math.PI * 2;
            const rad = Math.sqrt(rand()) * 0.028;
            push(podTheta + (Math.cos(ang) * rad) / R, R + 0.012 + 0.16, 0.0 + Math.sin(ang) * rad, KIND.LOGO_MARK);
          }
          break;
        }
        case KIND.LOGO_EDGE: {
          // Stadium-shaped padded patch with red edging.
          const t = rand() * Math.PI * 2;
          const a = 0.15;
          const b = 0.27;
          const ct = Math.cos(t);
          const st = Math.sin(t);
          const sx = Math.sign(ct) * Math.pow(Math.abs(ct), 0.7);
          const sy = Math.sign(st) * Math.pow(Math.abs(st), 0.7);
          push(logoTheta + (sx * a) / R, R + 0.02 + gauss() * 0.003, sy * b, kind);
          break;
        }
        case KIND.LOGO_MARK: {
          // Two small dots stacked: a nod to the mark, not a reproduction.
          const big = rand() < 0.62;
          const ang = rand() * Math.PI * 2;
          const rad = Math.sqrt(rand()) * (big ? 0.06 : 0.035);
          const cy = big ? -0.05 : 0.08;
          push(logoTheta + (Math.cos(ang) * rad) / R, R + 0.03, cy + Math.sin(ang) * rad, kind);
          break;
        }
      }
    }
  }
  // Shuffle so a partial draw range thins every part evenly.
  for (let a = i - 1; a > 0; a--) {
    const b = Math.floor(rand() * (a + 1));
    for (let c = 0; c < 4; c++) {
      const o = a * 4 + c;
      const p = b * 4 + c;
      [cyl[o], cyl[p]] = [cyl[p], cyl[o]];
      [seeds[o], seeds[p]] = [seeds[p], seeds[o]];
    }
  }
  return { cyl, seeds, count: i };
}

const RING_VERTEX_SHADER = /* glsl */ `
  uniform float uTime;
  uniform float uMorphA;
  uniform float uMorphB;
  uniform float uProgress;
  uniform float uGather;
  uniform float uBreath;
  uniform float uPhase;
  uniform float uWaveAmp;
  uniform float uLaneGap;
  uniform float uSize;
  uniform float uPixelRatio;
  uniform float uCenterDepth;
  uniform float uOpacity;
  uniform vec3 uRed;
  uniform vec3 uBone;
  uniform vec3 uGraphite;

  attribute vec4 aCyl;   // theta, radius, y, kind
  attribute vec4 aSeed;

  varying vec3 vColor;
  varying float vAlpha;

  const float PI = 3.14159265;

  float easeIO(float t) {
    return t < 0.5 ? 4.0 * t * t * t : 1.0 - pow(-2.0 * t + 2.0, 3.0) / 2.0;
  }

  // Rep-by-rep signals. Six reps span the strip; uPhase slides them along.
  float pressureWave(float xn, float t) {
    float ph = (xn * 0.5 + 0.5) * 6.0 * 2.0 * PI - t;
    return pow(0.5 - 0.5 * cos(ph), 1.8) - 0.32;
  }
  float movementWave(float xn, float t) {
    float ph = (xn * 0.5 + 0.5) * 6.0 * 2.0 * PI - t - 0.7;
    return 0.72 * sin(ph) + 0.2 * sin(3.0 * ph);
  }

  void main() {
    float theta = aCyl.x;
    float r = aCyl.y;
    float y = aCyl.z;
    float kind = aCyl.w;
    float R = 1.0;
    bool isRim = kind > 1.5 && kind < 2.5;
    float lane = y >= 0.0 ? 0.0 : 1.0; // top half -> pressure, bottom half -> movement

    // ---- Breathing: the cuff inflates, and a pressure pulse travels around it.
    float breath = pow(0.5 - 0.5 * cos(uTime * 2.0 * PI / 2.4), 1.6);
    float rr = R + (r - R) + R * uBreath * 0.03 * breath;
    float dr = rr - R;
    float travel = pow(0.5 + 0.5 * cos(theta - uTime * 1.4), 14.0) * uBreath;

    // ---- Stage A1: unroll the band into a strip (x = arc length).
    float u1 = easeIO(clamp(uMorphA * 2.0, 0.0, 1.0));
    float u2 = easeIO(clamp(uMorphA * 2.0 - 1.0, 0.0, 1.0));
    float s = theta * R;
    float Rc = R / max(1.0 - u1, 0.0015);
    float phi = s / Rc;
    vec3 p;
    p.x = (Rc + dr) * sin(phi);
    p.z = (Rc + dr) * cos(phi) - (Rc - R) - R * u1;
    p.y = y;

    // ---- Stage A2: collapse the strip into two live traces.
    float xn = s / (PI * R);
    float env = smoothstep(1.0, 0.72, abs(xn));
    float t = uPhase;
    float wave = lane < 0.5 ? pressureWave(xn, t) : movementWave(xn, t);
    float laneY = (lane < 0.5 ? 1.0 : -1.0) * uLaneGap;
    float spread = isRim ? 0.012 : (kind < 1.5 ? 0.075 : 0.04);
    float jitter = (aSeed.y + aSeed.z - 1.0) * spread;
    float amp = uWaveAmp * (1.0 - easeIO(uMorphB));
    float traceY = laneY + wave * amp * env + jitter;
    p.y = mix(p.y, traceY, u2);
    p.z = mix(p.z, (aSeed.w - 0.5) * 0.06, u2);

    // ---- Stage B: curl the traces into a face-on ring (clockwise from 12 o'clock).
    float v = easeIO(uMorphB);
    if (v > 0.0) {
      float k = v / R;
      float ang = k * s;
      vec2 c = k > 0.0001 ? vec2(sin(ang) / k, (1.0 - cos(ang)) / k) : vec2(s, 0.0);
      float ringOff = jitter * (isRim ? 1.4 : 0.9);
      float off = mix(p.y, ringOff, v);
      vec2 q = c + off * vec2(-sin(ang), cos(ang));
      q.y -= R * v;
      p.x = q.x;
      p.y = q.y;
      p.z *= (1.0 - v);
    }

    // ---- Intro gather from a scattered cloud.
    float g = easeIO(clamp(uGather * 1.8 - aSeed.x * 0.8, 0.0, 1.0));
    vec3 scatter = normalize(aSeed.yzw - 0.5 + 0.0001) * (2.4 + aSeed.x * 2.8);
    p = mix(scatter, p, g);

    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    gl_Position = projectionMatrix * mv;

    // ---- Color & alpha by part.
    vec3 col;
    float alpha;
    float size;
    if (kind < 0.5) {            // shell
      col = uBone * 0.42; alpha = 0.5; size = 1.0;
    } else if (kind < 1.5) {     // lining
      col = uBone * 0.2; alpha = 0.45; size = 0.9;
    } else if (kind < 2.5) {     // rim piping
      col = uRed; alpha = 1.0; size = 1.2;
    } else if (kind < 3.5) {     // sensor pod: glossy black body
      col = uBone * 0.3; alpha = 0.7; size = 1.0;
    } else if (kind < 4.5) {     // logo patch edging
      col = uRed; alpha = 0.95; size = 1.1;
    } else if (kind < 5.5) {     // logo mark
      col = uBone; alpha = 1.0; size = 1.0;
    } else {                     // pod edge: specular highlight
      col = uBone * 0.95; alpha = 0.9; size = 1.1;
    }
    col += travel * (isRim ? vec3(0.9, 0.35, 0.35) : uBone * 0.35);
    size += travel * 0.8;

    // Trace colors: pressure red, movement bone.
    vec3 laneCol = lane < 0.5 ? uRed : uBone * 0.9;
    float laneAlpha = isRim ? 1.0 : 0.28;
    col = mix(col, laneCol, u2);
    alpha = mix(alpha, laneAlpha, u2);
    size = mix(size, isRim ? 1.25 : 0.95, u2);

    // Dial: a hot arc up to uProgress, the rest a quiet graphite track.
    float f = (PI - s) / (2.0 * PI);
    float hot = 1.0 - smoothstep(uProgress - 0.003, uProgress + 0.003, f);
    vec3 dialCol = mix(uBone * 0.3, uRed, hot);
    float dialAlpha = mix(isRim ? 0.6 : 0.35, isRim ? 1.0 : 0.55, hot);
    col = mix(col, dialCol, v);
    alpha = mix(alpha, dialAlpha, v);
    float head = exp(-pow((f - uProgress) * 70.0, 2.0)) * v * step(0.002, uProgress);
    col += head * vec3(1.0, 0.55, 0.55);
    size += head * 1.4;

    // Depth cue for the 3D band: back of the ring recedes.
    float front = clamp((uCenterDepth - (-mv.z)) * 0.5 + 0.5, 0.0, 1.0);
    alpha *= mix(mix(0.3, 1.0, front), 1.0, u1);

    // Gentle shimmer.
    alpha *= 0.8 + 0.2 * sin(uTime * 1.7 + aSeed.w * 40.0);
    alpha *= mix(0.25, 1.0, g) * uOpacity;

    vColor = col;
    vAlpha = alpha;
    gl_PointSize = uSize * size * (0.65 + aSeed.w * 0.7) * uPixelRatio / -mv.z;
  }
`;

const RING_FRAGMENT_SHADER = /* glsl */ `
  varying vec3 vColor;
  varying float vAlpha;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float d = length(c);
    if (d > 0.5) discard;
    float a = smoothstep(0.5, 0.08, d);
    gl_FragColor = vec4(vColor, a * vAlpha);
  }
`;

function cssColor(THREE, value, fallback) {
  const c = new THREE.Color();
  try {
    c.set(value?.trim() || fallback);
  } catch {
    c.set(fallback);
  }
  return c;
}

/**
 * @param {any} THREE
 * @param {{ count: number, colorsFrom?: Element, samples?: { cyl: Float32Array, seeds: Float32Array, count: number } }} opts
 */
function createRingField(THREE, opts) {
  const data = opts.samples ?? buildCuffSamples(opts.count);
  const geometry = new THREE.BufferGeometry();
  // position is required by three; the shader uses aCyl instead.
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(data.count * 3), 3));
  geometry.setAttribute('aCyl', new THREE.BufferAttribute(data.cyl, 4));
  geometry.setAttribute('aSeed', new THREE.BufferAttribute(data.seeds, 4));

  const styles = opts.colorsFrom ? getComputedStyle(opts.colorsFrom) : null;
  const read = (name, fb) => cssColor(THREE, styles?.getPropertyValue(name), fb);

  const uniforms = {
    uTime: { value: 0 },
    uMorphA: { value: 0 },
    uMorphB: { value: 0 },
    uProgress: { value: 0 },
    uGather: { value: 1 },
    uBreath: { value: 0 },
    uPhase: { value: 0 },
    uWaveAmp: { value: 0.34 },
    uLaneGap: { value: 0.62 },
    uSize: { value: 9 },
    uPixelRatio: { value: 1 },
    uCenterDepth: { value: 5 },
    uOpacity: { value: 1 },
    uRed: { value: read('--c-ms-red', '#E10017') },
    uBone: { value: read('--c-ms-bone', '#F2F1EF') },
    uGraphite: { value: read('--c-ms-graphite', '#2B2B2E') },
  };

  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: RING_VERTEX_SHADER,
    fragmentShader: RING_FRAGMENT_SHADER,
    transparent: true,
    depthWrite: false,
    depthTest: false,
    blending: THREE.AdditiveBlending,
  });

  const points = new THREE.Points(geometry, material);
  points.frustumCulled = false;

  return {
    points,
    uniforms,
    count: data.count,
    /** Draw a fraction of particles (adaptive quality). Samples are shuffled by kind, so thin evenly. */
    setDensity(fraction) {
      geometry.setDrawRange(0, Math.floor(data.count * fraction));
    },
    dispose() {
      geometry.dispose();
      material.dispose();
    },
  };
}

// ---------------------------------------------------------------------------
// 7. Rep data (more than reps)
// Illustrative rep traces. The static SVG fallbacks come from snippets/c-ms-rep-path.liquid.
// Both people finish every rep on the same beat, so the counters stay in lockstep.
// Only how each rep is performed differs.
//   steady  : height, rhythm and shape hold from the first rep to the last
//   dropoff : reps get shallower and less even over the back half of the set
// ---------------------------------------------------------------------------

/** Share of the plot height a rep-1-depth rep fills. */
const TRACE_SCALE = 0.74;

const smoothstep = (a, b, x) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** Normalized movement depth (0 = standing, 1 = rep-1 depth) at time t ∈ [0, reps]. */
function repValue(profile, t, reps = 12) {
  if (t <= 0 || t >= reps) return 0;
  const k = Math.floor(t);
  const phi = t - k;
  // Brief pause at the top of each rep.
  const x = Math.min(1, Math.max(0, (phi - 0.05) / 0.9));
  if (x <= 0 || x >= 1) return 0;

  let amp;
  let warp = 1;
  let wobble = 0;
  if (profile === 'dropoff') {
    const d = smoothstep(3, reps - 1, k);
    amp = 1 - 0.42 * d;
    warp = 1 + 0.7 * d;
    wobble = 0.07 * smoothstep(4, reps - 1, k);
  } else {
    amp = 1 - 0.012 * Math.sin(k * 1.9) - 0.01 * (k / reps);
  }
  const xw = Math.pow(x, warp);
  let h = Math.pow(Math.sin(Math.PI * xw), 0.8) * amp;
  h += wobble * Math.sin(x * Math.PI * 6 + k * 1.3) * Math.sin(Math.PI * x);
  return Math.max(0, h);
}

/** Peak depth of rep index k (0-based). */
function repPeak(profile, k, reps = 12) {
  let peak = 0;
  for (let i = 1; i < 64; i++) peak = Math.max(peak, repValue(profile, k + i / 64, reps));
  return peak;
}

// ---------------------------------------------------------------------------
// 8. Sections
// ---------------------------------------------------------------------------

// ---- c-ms-hero: a solid, lit 3D cuff that inflates with a slow pressure "breath".
const hero = defineSection((root) => {
  const stage = root.querySelector('.c-ms-hero__stage');
  const fallback = root.querySelector('.c-ms-hero__fallback');
  if (!stage) return;

  if (!hasWebGL()) {
    stage.dataset.cMsGl = 'none';
    if (fallback) fallback.hidden = false;
    return;
  }

  const reduced = prefersReducedMotion();
  const pointer = { x: 0, y: 0, tx: 0, ty: 0 };
  const onPointer = (e) => {
    const r = root.getBoundingClientRect();
    pointer.tx = ((e.clientX - r.left) / r.width) * 2 - 1;
    pointer.ty = ((e.clientY - r.top) / r.height) * 2 - 1;
  };
  const trackPointer = isFinePointer() && !reduced;
  if (trackPointer) root.addEventListener('pointermove', onPointer, { passive: true });

  const scene = glScene(
    stage,
    async (THREE, { scene: s, camera, renderer }) => {
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.05;

      // Key light, a soft front fill and a red rim like the product renders.
      const key = new THREE.DirectionalLight(0xffffff, 2.4);
      key.position.set(2.5, 4, 5);
      const rim = new THREE.DirectionalLight(0xe10017, 3);
      rim.position.set(-4, 1.5, -3.5);
      const rim2 = new THREE.DirectionalLight(0xe10017, 1.6);
      rim2.position.set(4, -0.5, -3);
      const front = new THREE.DirectionalLight(0xffffff, 0.7);
      front.position.set(-1.5, 0.5, 5);
      const fill = new THREE.HemisphereLight(0x8a8a94, 0x000000, 0.35);
      s.add(key, front, rim, rim2, fill);

      // Procedural cuff until a decimated GLB of the real product is available.
      const cuff = buildCuff(THREE);
      const inflate = new THREE.Group();
      inflate.add(cuff.object);
      const tilt = new THREE.Group();
      tilt.add(inflate);
      s.add(tilt);
      // Compile shaders off the main thread where supported (KHR_parallel_shader_compile).
      if (renderer.extensions.has('KHR_parallel_shader_compile')) await renderer.compileAsync(s, camera);

      let introStart = 0;
      return {
        resize() {
          const share = isSmallViewport() ? 0.78 : 0.56;
          const d = fitDistance(camera, 2.4 / share, 2.1 / share);
          camera.position.set(0, 0.05, Math.max(5, d));
          camera.lookAt(0, 0, 0);
        },
        update(dt, time) {
          // Pressure breath: the band inflates slightly, the piping and red rim glow with it.
          const b = reduced ? 0 : Math.pow(0.5 - 0.5 * Math.cos((time * 2 * Math.PI) / 2.6), 1.6);
          inflate.scale.set(1 + 0.022 * b, 1 + 0.006 * b, 1 + 0.022 * b);
          cuff.setPressure(b);
          rim.intensity = 2.2 + 1.6 * b;

          // Intro: turn into place and settle (wall-clock, so slow devices finish on time).
          let intro = 1;
          if (!reduced) {
            const now = performance.now();
            introStart ||= now;
            intro = easeOut(Math.min(1, (now - introStart) / 1600));
          }
          tilt.scale.setScalar(0.9 + 0.1 * intro);

          pointer.x = damp(pointer.x, pointer.tx, 3, dt);
          pointer.y = damp(pointer.y, pointer.ty, 3, dt);
          const idle = reduced ? 0 : Math.sin(time * 0.22) * 0.26;
          tilt.rotation.set(0.36 + pointer.y * 0.08, -0.4 + idle + pointer.x * 0.25 - (1 - intro) * 1.3, -0.04);
        },
        dispose() {
          cuff.dispose();
        },
      };
    },
    { staticFrame: reduced, antialias: true }
  );

  // The canvas is decorative: start it after load so it never competes with first paint.
  let stopLifecycle = () => {};
  const cancelIdle = whenIdle(() => {
    stopLifecycle = lifecycle(stage, scene, { near: '50% 0px' });
  });

  return () => {
    cancelIdle();
    stopLifecycle();
    if (trackPointer) root.removeEventListener('pointermove', onPointer);
  };
});

// ---- c-ms-missing-metric: familiar metrics dim as the empty muscle slot takes focus.
const missingMetric = defineSection((root) => {
  const strip = root.querySelector('.c-ms-mm__strip');
  const cleanups = [inViewClass(root)];

  if (strip && canChoreograph()) {
    const { ScrollTrigger } = getGsap();
    root.style.setProperty('--c-ms-p', '0');
    const st = ScrollTrigger.create({
      trigger: strip,
      start: 'top 85%',
      end: 'bottom 45%',
      scrub: true,
      onUpdate: (self) => root.style.setProperty('--c-ms-p', self.progress.toFixed(3)),
    });
    cleanups.push(() => {
      st.kill();
      root.style.removeProperty('--c-ms-p');
    });
  }

  return () => cleanups.forEach((fn) => fn());
});

// ---- c-ms-how-it-works: lower/upper body toggle. Swaps the placement photo and step copy,
// and shares the choice with the score section through setRegion().
const howItWorks = defineSection((root) => {
  const radios = [...root.querySelectorAll('.c-ms-hiw__radio')];
  const variants = [...root.querySelectorAll('[data-c-ms-region]')];
  const photos = [...root.querySelectorAll('[data-c-ms-photo]')];

  const apply = (next) => {
    root.dataset.cMsActiveRegion = next;
    variants.forEach((el) => (el.hidden = el.dataset.cMsRegion !== next));
    // Photos crossfade, so they're toggled by class; the inactive one is hidden from assistive tech.
    photos.forEach((wrap) => {
      const on = wrap.dataset.cMsPhoto === next;
      wrap.classList.toggle('is-active', on);
      if (on) wrap.removeAttribute('aria-hidden');
      else wrap.setAttribute('aria-hidden', 'true');
    });
    setRegion(next);
  };

  const onChange = (e) => {
    if (e.target.checked) apply(e.target.value);
  };
  radios.forEach((r) => r.addEventListener('change', onChange));

  const checked = radios.find((r) => r.checked);
  if (checked) apply(checked.value);

  const stopInView = inViewClass(root);
  return () => {
    radios.forEach((r) => r.removeEventListener('change', onChange));
    stopInView();
  };
});

// ---- c-ms-signal: the cuff unwraps into pressure and movement traces (pinned, scrubbed).
// The scene lives in the free band below the copy. Without pinning (narrow viewports, too
// little room below the copy, no WebGL, reduced motion) the static traces draw in instead.
const SIG_MIN_BAND = 180; // px of free band the pinned scene needs
const SIG_BAND_GAP = 24; // px kept clear above and below the scene
const SIG_LABEL_PX = 30; // the trace labels sit this far above the top trace

// The copy's bottom edge, in px from the top of the frame.
const signalCopyBottom = (frame, copy) => copy.getBoundingClientRect().bottom - frame.getBoundingClientRect().top;

const signal = defineSection((root) => {
  const frame = root.querySelector('.c-ms-sig__frame');
  const stage = root.querySelector('.c-ms-sig__stage');
  const copy = root.querySelector('.c-ms-sig__copy');
  const labels = {
    pressure: root.querySelector('.c-ms-sig__label--pressure'),
    movement: root.querySelector('.c-ms-sig__label--movement'),
  };
  const fits = () =>
    measureLive(
      root,
      () => frame.clientHeight + stickyTop() - signalCopyBottom(frame, copy) - 2 * SIG_BAND_GAP >= SIG_MIN_BAND
    );
  if (!frame || !stage || !copy || !canPin() || !hasWebGL() || !fits()) {
    root.dataset.cMsMode = 'static';
    const traces = root.querySelector('.c-ms-sig__static');
    const stopInView = traces ? inViewClass(traces, 'is-inview', '0px 0px -15% 0px') : () => {};
    return () => {
      stopInView();
      delete root.dataset.cMsMode;
    };
  }
  root.dataset.cMsMode = 'live';

  const { ScrollTrigger } = getGsap();
  const progress = { target: 0, value: 0 };
  const st = ScrollTrigger.create({
    trigger: root,
    start: () => `top ${headerOffset()}px`,
    end: () => `+=${window.innerHeight * (isSmallViewport() ? 1.4 : 2)}`,
    pin: frame,
    scrub: true,
    invalidateOnRefresh: true,
    onUpdate: (self) => (progress.target = self.progress),
  });

  const scene = glScene(stage, (THREE, { scene: s, camera }) => {
    const field = createRingField(THREE, { count: particleBudget(16000, 7000), colorsFrom: root });
    const group = new THREE.Group();
    group.add(field.points);
    s.add(group);
    const u = field.uniforms;
    u.uPixelRatio.value = pixelRatio();
    u.uLaneGap.value = 0.5;

    let dRing = 6;
    let dTrace = 8;
    let sizeBase = 9;
    let phase = 0;
    let squash = 1; // vertical scale of the traces on short screens
    let labelLeft = 0; // the labels' CSS left edge (the content gutter), in stage px
    const band = { top: 0, height: 1 }; // free band below the copy, in stage px
    let size = { w: 1, h: 1 };
    const v = new THREE.Vector3();

    // World heights of what's on screen: the tilted cuff, and both traces at full amplitude.
    const CUFF_H = 1.7;
    const TRACES_H = 1.8;

    // Fit the cuff and the traces into the band: first flatten the traces (down to half
    // height, so they keep the full width), then move the camera back if they still don't fit.
    const fit = () => {
      const { w, h } = size;
      if (h <= 1) return; // not sized yet
      labelLeft = labels.pressure.offsetLeft;
      band.top = signalCopyBottom(frame, copy) + SIG_BAND_GAP;
      band.height = Math.max(SIG_MIN_BAND / 2, h - SIG_BAND_GAP - band.top);
      const pxAtOne = h / viewHeight(camera, 1); // px per world unit at camera distance 1
      dRing = Math.max(fitDistance(camera, 2.6 / (w < h ? 0.85 : 0.55), 2.2 / 0.55), (CUFF_H * pxAtOne) / band.height);
      dTrace = Math.min(fitDistance(camera, Math.PI * 2 * 1.08, 1), isSmallViewport() ? 7.5 : 9.5);
      const room = band.height - SIG_LABEL_PX;
      squash = clamp((room * dTrace) / (TRACES_H * pxAtOne), 0.5, 1);
      dTrace = Math.max(dTrace, (TRACES_H * squash * pxAtOne) / room);
      u.uLaneGap.value = 0.5 * squash;
      u.uWaveAmp.value = 0.34 * squash;
    };
    // The copy can reflow on its own (web fonts), so re-fit when it does.
    const copyRO = new ResizeObserver(() => fit());
    copyRO.observe(copy);

    // Labels sit at the content gutter, or at the start of the traces when those end up narrower.
    const TRACE_START = -Math.PI * 0.95; // where a trace fades in (world x)
    const placeLabel = (el, laneY, opacity, w, h) => {
      v.set(TRACE_START, laneY, 0).applyMatrix4(group.matrixWorld).project(camera);
      const x = Math.max(0, (v.x * 0.5 + 0.5) * w - labelLeft);
      const y = (-v.y * 0.5 + 0.5) * h;
      el.style.transform = `translate3d(${x.toFixed(1)}px, ${(y - 28).toFixed(1)}px, 0)`;
      el.style.opacity = opacity.toFixed(3);
    };

    return {
      resize(w, h) {
        size = { w, h };
        fit();
        sizeBase = Math.max(7, Math.min(12, (h / 800) * 10));
      },
      update(dt, time, ctx) {
        progress.value = damp(progress.value, progress.target, 10, dt);
        const p = progress.value;
        const turn = easeInOut(range(p, 0, 0.24));
        const morph = range(p, 0.14, 0.78);
        const flow = range(p, 0.62, 0.9);

        u.uTime.value = time;
        u.uBreath.value = 1 - range(p, 0, 0.14);
        u.uMorphA.value = morph;
        phase += dt * (0.4 + flow * 2.4);
        u.uPhase.value = phase;

        const d = dRing + (dTrace - dRing) * easeInOut(range(p, 0.1, 0.7));
        camera.position.set(0, 0, d);
        camera.lookAt(0, 0, 0);
        u.uCenterDepth.value = d;
        u.uSize.value = sizeBase * (1 + 0.25 * easeInOut(morph));

        // Sit below the copy block, easing further down as the traces form...
        const vh = viewHeight(camera, d);
        const drop = isSmallViewport() ? 0.16 : 0.1 + 0.07 * easeInOut(range(p, 0.4, 0.8));
        let y = -vh * drop;
        // ...but never reach up into the copy (the band's top), labels included.
        const pxPerWorld = ctx.height / vh;
        const traces = easeInOut(clamp(morph * 2 - 1)); // 0 while it's the cuff, 1 once the traces form
        const halfH = (CUFF_H + (TRACES_H * squash - CUFF_H) * traces) / 2;
        const top = ctx.height / 2 - (y + halfH) * pxPerWorld - SIG_LABEL_PX * traces;
        if (top < band.top) y -= (band.top - top) / pxPerWorld;
        group.position.y = y;
        group.rotation.set(0.42 * (1 - turn), -0.35 * (1 - turn), -0.05 * (1 - turn));
        group.updateMatrixWorld();

        // Labels ride just above each trace's peaks.
        const labelOpacity = range(p, 0.68, 0.82);
        const gap = u.uLaneGap.value;
        placeLabel(labels.pressure, gap + 0.34 * squash, labelOpacity, ctx.width, ctx.height);
        placeLabel(labels.movement, -gap + 0.34 * squash, labelOpacity, ctx.width, ctx.height);
      },
      degrade() {
        field.setDensity(0.55);
      },
      dispose() {
        copyRO.disconnect();
        field.dispose();
      },
    };
  });

  // Keep the server-rendered state readable if the GPU or Three.js fails.
  const createScene = scene.create;
  scene.create = async () => {
    try {
      await createScene();
    } catch (error) {
      scene.dispose();
      st.kill(true);
      root.dataset.cMsMode = 'static';
      root.querySelector('.c-ms-sig__static')?.classList.add('is-inview');
      throw error;
    }
  };
  const stop = lifecycle(root, scene);
  return () => {
    stop();
    st.kill(true);
    delete root.dataset.cMsMode;
  };
});

// ---- c-ms-more-than-reps: two people, same rep count, different performance.
// One shared playhead (0..reps) drives both canvases. It's set by scroll (when
// the section fits the viewport and gets pinned), by dragging on a lane, by the
// range input, or by Replay. Canvases redraw only when the playhead moves.
const SAMPLES_PER_REP = 48;

const moreThanReps = defineSection((root, settings) => {
  const reps = Number(settings.reps) || 12;
  const controls = root.querySelector('.c-ms-mtr__controls');
  if (controls) controls.hidden = false;
  const frame = root.querySelector('.c-ms-mtr__frame');
  const inner = root.querySelector('.c-ms-mtr__inner');
  const scrub = root.querySelector('.c-ms-mtr__scrub');
  const replayBtn = root.querySelector('.c-ms-mtr__replay');
  const live = root.querySelector('[data-c-ms-live]');
  const reduced = prefersReducedMotion();
  const choreo = canChoreograph();
  // The server renders the finished set (is-complete) for no-JS. Reset it right away and without
  // the result rows' collapse transition (phones), so the page height is final before the pins below
  // measure it; otherwise they end up ~200px off once the rows finish collapsing.
  if (!reduced) {
    root.classList.add('is-resetting');
    root.classList.remove('is-complete');
    void root.offsetHeight;
    root.classList.remove('is-resetting');
  }
  const styles = getComputedStyle(root);
  const token = (name) => styles.getPropertyValue(name).trim();
  const colors = {
    bone: token('--c-ms-bone') || '#F2F1EF',
    mist: token('--c-ms-mist') || '#A1A1A6',
    graphite: token('--c-ms-graphite') || '#2B2B2E',
    red: token('--c-ms-red') || '#E10017',
  };
  const font = token('--c-ms-font-body') || 'Inter, sans-serif';
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  // ---- Lanes: canvas + precomputed samples ----
  const lanes = [...root.querySelectorAll('.c-ms-mtr__lane')].map((el, i) => {
    const profile = el.dataset.cMsProfile;
    const plot = el.querySelector('.c-ms-mtr__plot');
    const canvas = document.createElement('canvas');
    canvas.className = 'c-ms-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    plot.appendChild(canvas);
    plot.querySelector('.c-ms-mtr__static')?.setAttribute('hidden', '');
    const n = reps * SAMPLES_PER_REP;
    const values = new Float32Array(n + 1);
    for (let s = 0; s <= n; s++) values[s] = repValue(profile, (s / n) * reps, reps);
    const peaks = Array.from({ length: reps }, (_, k) => {
      let best = 0;
      let at = k + 0.5;
      for (let s = 1; s < SAMPLES_PER_REP; s++) {
        const v = values[k * SAMPLES_PER_REP + s];
        if (v > best) {
          best = v;
          at = k + s / SAMPLES_PER_REP;
        }
      }
      return { t: at, v: best };
    });
    return {
      el,
      plot,
      canvas,
      ctx: canvas.getContext('2d'),
      count: el.querySelector('[data-c-ms-count]'),
      person: settings.people?.[i] ?? { label: `Person ${i + 1}`, checkpoint: '' },
      values,
      peaks,
      ref: repPeak(profile, 0, reps),
      w: 0,
      h: 0,
    };
  });

  let P = 0;
  let frameReq = 0;
  let lastCheckpoint = 0;

  const draw = (lane) => {
    const { ctx, w, h, values, peaks, ref } = lane;
    if (!w || !h) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const pad = 8;
    const x = (t) => (t / reps) * w;
    const y = (v) => h - pad - v * (h - pad * 2) * TRACE_SCALE;
    const n = values.length - 1;

    // Rep ticks along the floor.
    ctx.fillStyle = colors.graphite;
    for (let k = 1; k < reps; k++) ctx.fillRect(Math.round(x(k)) - 0.5, h - 5, 1, 5);
    ctx.fillRect(0, h - 1, w, 1);

    // Rep-1 reference level.
    const ry = Math.round(y(ref)) + 0.5;
    ctx.save();
    ctx.setLineDash([3, 5]);
    ctx.strokeStyle = colors.mist;
    ctx.globalAlpha = 0.45;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, ry);
    ctx.lineTo(w, ry);
    ctx.stroke();
    ctx.restore();
    ctx.font = `600 10px ${font}`;
    ctx.fillStyle = colors.mist;
    ctx.textAlign = 'right';
    ctx.fillText(settings.reference_label || 'Rep 1 level', w, ry - 6);

    if (P <= 0) return;

    // Trace up to the playhead.
    const end = (P / reps) * n;
    const last = Math.floor(end);
    const path = new Path2D();
    path.moveTo(0, y(values[0]));
    for (let s = 1; s <= last; s++) path.lineTo(x((s / n) * reps), y(values[s]));
    const frac = end - last;
    const vEnd = last < n ? values[last] + (values[last + 1] - values[last]) * frac : values[n];
    path.lineTo(x(P), y(vEnd));

    const area = new Path2D(path);
    area.lineTo(x(P), h);
    area.lineTo(0, h);
    area.closePath();
    const grad = ctx.createLinearGradient(0, y(1), 0, h);
    grad.addColorStop(0, 'rgba(242,241,239,0.10)');
    grad.addColorStop(1, 'rgba(242,241,239,0)');
    ctx.fillStyle = grad;
    ctx.fill(area);

    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = colors.bone;
    ctx.globalAlpha = 0.12;
    ctx.lineWidth = 7;
    ctx.stroke(path);
    ctx.globalAlpha = 1;
    ctx.lineWidth = 2;
    ctx.stroke(path);

    // Peak markers for completed reps.
    ctx.fillStyle = colors.bone;
    for (const pk of peaks) {
      if (pk.t > P) break;
      ctx.beginPath();
      ctx.arc(x(pk.t), y(pk.v), 2.6, 0, Math.PI * 2);
      ctx.fill();
    }

    // Playhead.
    if (P < reps) {
      const px = Math.round(x(P)) + 0.5;
      ctx.strokeStyle = colors.red;
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(px, 0);
      ctx.lineTo(px, h);
      ctx.stroke();
      ctx.globalAlpha = 0.25;
      ctx.fillStyle = colors.red;
      ctx.beginPath();
      ctx.arc(x(P), y(vEnd), 9, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(x(P), y(vEnd), 4, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  const announce = (done) => {
    const checkpoint = done >= reps ? reps : done >= 8 ? 8 : done >= 4 ? 4 : 0;
    if (checkpoint < lastCheckpoint) lastCheckpoint = checkpoint;
    if (checkpoint > lastCheckpoint && live) {
      lastCheckpoint = checkpoint;
      live.textContent =
        `Rep ${checkpoint} of ${reps}. ` + lanes.map((l) => `${l.person.label}: ${l.person.checkpoint}.`).join(' ');
    }
  };

  const render = () => {
    frameReq = 0;
    const done = P >= reps - 1e-3 ? reps : Math.floor(P);
    lanes.forEach((lane) => {
      draw(lane);
      if (lane.count) lane.count.textContent = String(done).padStart(2, '0');
    });
    if (scrub && document.activeElement !== scrub) scrub.value = String(Math.round(P));
    if (scrub) {
      scrub.style.setProperty('--c-ms-fill', `${(P / reps) * 100}%`);
      scrub.setAttribute('aria-valuetext', `Rep ${Math.round(P)} of ${reps}`);
    }
    root.classList.toggle('is-complete', P >= reps - 1e-3);
    announce(done);
  };

  const setP = (value) => {
    P = clamp(value, 0, reps);
    if (!frameReq) frameReq = requestAnimationFrame(render);
  };

  // ---- Sizing ----
  const ro = new ResizeObserver(() => {
    lanes.forEach((lane) => {
      lane.w = lane.plot.clientWidth;
      lane.h = lane.plot.clientHeight;
      lane.canvas.width = Math.round(lane.w * dpr);
      lane.canvas.height = Math.round(lane.h * dpr);
    });
    render();
  });
  lanes.forEach((lane) => ro.observe(lane.plot));

  // ---- Replay ----
  let replayRaf = 0;
  const stopReplay = () => {
    cancelAnimationFrame(replayRaf);
    replayRaf = 0;
  };
  const replay = () => {
    stopReplay();
    if (reduced) {
      setP(reps);
      return;
    }
    const start = performance.now();
    const duration = 7000;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setP(t * reps);
      if (t < 1) replayRaf = requestAnimationFrame(step);
      else replayRaf = 0;
    };
    replayRaf = requestAnimationFrame(step);
  };
  replayBtn?.addEventListener('click', replay);

  // ---- Direct manipulation: drag across either lane ----
  let dragging = null;
  const fromPointer = (e, lane) => {
    const r = lane.plot.getBoundingClientRect();
    setP(((e.clientX - r.left) / r.width) * reps);
  };
  const onDown = (lane) => (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    stopReplay();
    dragging = lane;
    lane.el.setPointerCapture?.(e.pointerId);
    fromPointer(e, lane);
  };
  const onMove = (e) => dragging && fromPointer(e, dragging);
  const onUp = () => (dragging = null);
  const downHandlers = lanes.map((lane) => {
    const h = onDown(lane);
    lane.el.addEventListener('pointerdown', h);
    lane.el.addEventListener('pointermove', onMove);
    lane.el.addEventListener('pointerup', onUp);
    lane.el.addEventListener('pointercancel', onUp);
    return h;
  });

  const onScrub = () => {
    stopReplay();
    setP(Number(scrub.value));
  };
  scrub?.addEventListener('input', onScrub);

  // ---- Scroll: pin and let the scroll drive the reps; autoplay once in view if nothing fits ----
  // Desktop pins the whole set. Phones can't fit the copy and both lanes, so they pin just
  // the lanes (the copy scrolls past first, the controls and closing line follow).
  const cleanups = [];
  const lanesEl = root.querySelector('.c-ms-mtr__lanes');
  const measurePinned = (mode, measure) => {
    root.dataset.cMsPinned = mode;
    const result = measure();
    delete root.dataset.cMsPinned;
    return result;
  };
  if (reduced) {
    setP(reps);
  } else {
    setP(0);
    const room = window.innerHeight - headerOffset() + stickyTop();
    let pinMode = null;
    if (choreo && inner && measurePinned('frame', () => inner.offsetHeight + 64 <= room)) pinMode = 'frame';
    else if (choreo && lanesEl) {
      // Short screens: shorten the rep plots (down to 48px) until the lanes fit the screen.
      const plotH = measurePinned('lanes', () => {
        const plots = [...lanesEl.querySelectorAll('.c-ms-mtr__plot')];
        if (!plots.length) return 0;
        const rows = new Set(plots.map((p) => p.offsetTop)).size; // stacked on phones, side by side on desktop
        const fixed = lanesEl.offsetHeight - plots[0].offsetHeight * rows;
        const fit = Math.floor((room - 32 - fixed) / rows);
        return fit >= 48 ? Math.min(fit, plots[0].offsetHeight) : 0;
      });
      if (plotH) {
        pinMode = 'lanes';
        root.style.setProperty('--c-ms-mtr-plot-h', `${plotH}px`);
      }
    }
    if (pinMode) {
      root.dataset.cMsPinned = pinMode;
      const { ScrollTrigger } = getGsap();
      const byLanes = pinMode === 'lanes';
      const st = ScrollTrigger.create({
        trigger: byLanes ? lanesEl : root,
        // The lanes pin centred in the space below the header.
        start: () =>
          byLanes
            ? `top ${headerOffset() + Math.max(16, (window.innerHeight - headerOffset() - lanesEl.offsetHeight) / 2)}px`
            : `top ${headerOffset()}px`,
        end: () => `+=${window.innerHeight * 2.2}`,
        pin: byLanes ? lanesEl : frame,
        scrub: true,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          if (dragging) return;
          stopReplay();
          setP(range(self.progress, 0.04, 0.88) * reps);
        },
      });
      cleanups.push(() => {
        st.kill(true);
        delete root.dataset.cMsPinned;
        root.style.removeProperty('--c-ms-mtr-plot-h');
      });
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting) && P === 0) {
            replay();
            io.disconnect();
          }
        },
        { threshold: 0.45 }
      );
      io.observe(lanesEl ?? root);
      cleanups.push(() => io.disconnect());
    }
  }

  return () => {
    stopReplay();
    cancelAnimationFrame(frameReq);
    ro.disconnect();
    cleanups.forEach((fn) => fn());
    replayBtn?.removeEventListener('click', replay);
    scrub?.removeEventListener('input', onScrub);
    lanes.forEach((lane, i) => {
      lane.el.removeEventListener('pointerdown', downHandlers[i]);
      lane.el.removeEventListener('pointermove', onMove);
      lane.el.removeEventListener('pointerup', onUp);
      lane.el.removeEventListener('pointercancel', onUp);
      lane.canvas.remove();
      lane.plot.querySelector('.c-ms-mtr__static')?.removeAttribute('hidden');
    });
    root.classList.add('is-complete');
  };
});

// ---- c-ms-score: the signal curls back into a ring, which lands on the card's outer
// ring (lower body). Then the card resolves, with both regions filling in.
const score = defineSection((root, settings) => {
  const frame = root.querySelector('.c-ms-score__frame');
  const stage = root.querySelector('.c-ms-score__stage');
  const dial = root.querySelector('.c-ms-score__dial');
  const grid = root.querySelector('.c-ms-score__grid');

  // Desktop puts the copy and the card side by side, and both must fit the pinned frame. Phones
  // stack them and the frame grows to fit (see the CSS), so only the card has to fit the screen:
  // a frame taller than the screen pins by its bottom edge (below) and the card stays in view.
  const card = root.querySelector('.c-ms-score__card');
  const fits = () =>
    measureLive(root, () => {
      const cs = getComputedStyle(frame);
      const pad = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      const room = window.innerHeight - headerOffset() + stickyTop();
      const stacked = getComputedStyle(grid).gridTemplateColumns.trim().split(/\s+/).length < 2;
      if (stacked) return card.offsetHeight + pad <= room;
      const items = [...grid.children].filter((el) => el.offsetParent !== null).map((el) => el.offsetHeight);
      return Math.max(0, ...items) + pad <= room;
    });
  const nums =[...root.querySelectorAll('[data-c-ms-num]')].map((el) => ({ el, value: Number(el.dataset.cMsValue) || 0, shown: -1 }));
  const lowerPct = (nums.find((n) => n.el.dataset.cMsNum === 'lower')?.value ?? 0) / 100;
  const ringRatio = Number(settings.ring_ratio) || 0.86;

  const showNumbers = (t) => {
    for (const n of nums) {
      const v = Math.round(n.value * t);
      if (v !== n.shown) {
        n.el.textContent = String(v);
        n.shown = v;
      }
    }
  };

  const offRegion = onRegionChange((next) => (root.dataset.cMsActiveRegion = next));
  root.dataset.cMsActiveRegion = getRegion();

  // ---- Static: the rings draw in (CSS transition) and the numbers count up, once.
  if (!frame || !stage || !dial || !grid || !card || !canPin() || !hasWebGL() || !fits()) {
    root.dataset.cMsMode = 'static';
    if (prefersReducedMotion()) {
      root.classList.add('is-inview');
      return () => {
        offRegion();
        root.classList.remove('is-inview');
        delete root.dataset.cMsMode;
      };
    }
    showNumbers(0);
    let raf = 0;
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        root.classList.add('is-inview');
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min(1, (now - start) / 1600);
          showNumbers(easeOut(t));
          if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.35 }
    );
    io.observe(dial);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
      offRegion();
      showNumbers(1);
      root.classList.remove('is-inview');
      delete root.dataset.cMsMode;
    };
  }

  // ---- Live: pinned, scroll-scrubbed.
  root.dataset.cMsMode = 'live';
  showNumbers(0);
  root.style.setProperty('--c-ms-fill', '0');

  const { ScrollTrigger } = getGsap();
  const progress = { target: 0, value: 0 };
  const st = ScrollTrigger.create({
    trigger: frame,
    // On short phones the stacked frame is taller than the screen: pin it by its bottom edge so the
    // card (where the ring lands) is fully visible; the copy above has been read on the way in.
    start: () => (frame.offsetHeight > window.innerHeight - headerOffset() + 1 ? 'bottom bottom' : `top ${headerOffset()}px`),
    end: () => `+=${window.innerHeight * (isSmallViewport() ? 1.4 : 1.8)}`,
    pin: frame,
    scrub: true,
    invalidateOnRefresh: true,
    onUpdate: (self) => (progress.target = self.progress),
  });

  let lastFill = -1;
  const scene = glScene(stage, (THREE, { scene: s, camera }) => {
    const field = createRingField(THREE, { count: particleBudget(16000, 7000), colorsFrom: root });
    const group = new THREE.Group();
    group.add(field.points);
    s.add(group);
    const u = field.uniforms;
    u.uPixelRatio.value = pixelRatio();
    u.uMorphA.value = 1;
    u.uLaneGap.value = 0.5;

    let d = 9;
    let sizeBase = 9;
    let phase = 0;
    const dialTarget = { x: 0, y: 0, r: 1 };

    const measureDial = (w, h) => {
      // Map the outer ring (DOM) to world space on the z = 0 plane.
      const sr = stage.getBoundingClientRect();
      const dr = dial.getBoundingClientRect();
      const vh = viewHeight(camera, d);
      const pxToWorld = vh / h;
      dialTarget.x = (dr.left + dr.width / 2 - (sr.left + w / 2)) * pxToWorld;
      dialTarget.y = -(dr.top + dr.height / 2 - (sr.top + h / 2)) * pxToWorld;
      dialTarget.r = (dr.width / 2) * ringRatio * pxToWorld;
    };

    return {
      resize(w, h) {
        d = Math.min(fitDistance(camera, Math.PI * 2 * 1.08, 1), isSmallViewport() ? 7.5 : 9.5);
        camera.position.set(0, 0, d);
        camera.lookAt(0, 0, 0);
        u.uCenterDepth.value = d;
        sizeBase = Math.max(7, Math.min(12, (h / 800) * 10));
        measureDial(w, h);
      },
      update(dt, time) {
        progress.value = damp(progress.value, progress.target, 10, dt);
        const p = progress.value;
        const curl = range(p, 0.06, 0.55);
        const v = easeInOut(curl);
        const fill = easeInOut(range(p, 0.58, 0.92));
        const readout = range(p, 0.5, 0.62);

        u.uTime.value = time;
        u.uMorphB.value = curl;
        phase += dt * (2.6 * (1 - v) + 0.2);
        u.uPhase.value = phase;
        u.uProgress.value = fill * lowerPct;
        // The particles hand over to the card's crisp rings, leaving a soft halo.
        u.uOpacity.value = 1 - 0.7 * readout;
        u.uSize.value = sizeBase * (1.2 + 0.25 * v);

        group.position.set(dialTarget.x * v, dialTarget.y * v, 0);
        group.scale.setScalar(1 + (dialTarget.r - 1) * v);

        root.style.setProperty('--c-ms-readout', readout.toFixed(3));
        if (Math.abs(fill - lastFill) > 0.001) {
          root.style.setProperty('--c-ms-fill', fill.toFixed(3));
          showNumbers(fill);
          lastFill = fill;
        }
      },
      degrade() {
        field.setDensity(0.55);
      },
      dispose() {
        field.dispose();
      },
    };
  });

  // Keep the server-rendered state readable if the GPU or Three.js fails.
  const createScene = scene.create;
  scene.create = async () => {
    try {
      await createScene();
    } catch (error) {
      scene.dispose();
      st.kill(true);
      root.dataset.cMsMode = 'static';
      showNumbers(1);
      root.classList.add('is-inview');
      root.style.removeProperty('--c-ms-readout');
      root.style.removeProperty('--c-ms-fill');
      throw error;
    }
  };
  const stop = lifecycle(root, scene);
  return () => {
    stop();
    st.kill(true);
    offRegion();
    root.style.removeProperty('--c-ms-readout');
    root.style.removeProperty('--c-ms-fill');
    showNumbers(1);
    delete root.dataset.cMsMode;
  };
});

// ---- c-ms-trend: the example check-in line draws in with scroll.

// Points form one tab stop; arrow keys move between check-ins (roving tabindex).
function rovingFocus(points) {
  let current = points.length - 1;
  const set = (i, focus) => {
    current = (i + points.length) % points.length;
    points.forEach((pt, k) => (pt.tabIndex = k === current ? 0 : -1));
    if (focus) points[current].focus();
  };
  const onKey = (e) => {
    const i = points.indexOf(e.target);
    if (i < 0) return;
    const map = { ArrowRight: i + 1, ArrowUp: i + 1, ArrowLeft: i - 1, ArrowDown: i - 1, Home: 0, End: points.length - 1 };
    if (!(e.key in map)) return;
    e.preventDefault();
    set(map[e.key], true);
  };
  set(current, false);
  points.forEach((pt) => pt.addEventListener('keydown', onKey));
  return () => points.forEach((pt) => {
    pt.removeEventListener('keydown', onKey);
    pt.tabIndex = 0;
  });
}

const trend = defineSection((root) => {
  const plot = root.querySelector('.c-ms-trend__plot');
  const points = [...root.querySelectorAll('.c-ms-trend__pt')];
  if (!plot || !points.length) return;
  const stopRoving = rovingFocus(points);
  if (!canChoreograph()) return stopRoving;

  const { ScrollTrigger } = getGsap();
  const last = Math.max(1, points.length - 1);
  root.classList.add('is-drawing');

  const apply = (p) => {
    root.style.setProperty('--c-ms-p', p.toFixed(3));
    points.forEach((pt, i) => pt.classList.toggle('is-on', p >= i / last - 0.02));
  };
  apply(0);

  const st = ScrollTrigger.create({
    trigger: plot,
    start: 'top 80%',
    end: 'bottom 50%',
    scrub: true,
    onUpdate: (self) => apply(self.progress),
  });

  return () => {
    stopRoving();
    st.kill();
    root.classList.remove('is-drawing');
    root.style.removeProperty('--c-ms-p');
    points.forEach((pt) => pt.classList.remove('is-on'));
  };
});

// ---- Data links, shared by c-ms-device-hero and c-ms-how-scores: an elbowed line that draws in,
// a port that pops where it lands, and (once live) a packet that runs along it on a loop.
// CSS owns the draw/pop transitions through the section's own class prefix.

const SVG_NS = 'http://www.w3.org/2000/svg';
let linkCount = 0;

/** Path through the points with rounded elbows (consecutive duplicates are dropped). */
function elbowPath(points, radius = 12) {
  const pts = points.filter((p, i) => i === 0 || Math.hypot(p[0] - points[i - 1][0], p[1] - points[i - 1][1]) > 0.5);
  let d = `M${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const [px, py] = pts[i - 1];
    const [cx, cy] = pts[i];
    const [nx, ny] = pts[i + 1];
    const l1 = Math.hypot(cx - px, cy - py);
    const l2 = Math.hypot(nx - cx, ny - cy);
    const r = Math.min(radius, l1 / 2, l2 / 2);
    d += ` L${cx + ((px - cx) / l1) * r} ${cy + ((py - cy) / l1) * r} Q${cx} ${cy} ${cx + ((nx - cx) / l2) * r} ${cy + ((ny - cy) / l2) * r}`;
  }
  const last = pts[pts.length - 1];
  return `${d} L${last[0]} ${last[1]}`;
}

/**
 * @param {SVGSVGElement} svg - Overlay whose viewBox matches its box in CSS px.
 * @param {number[][]} points - From the source to the port.
 * @param {{ prefix: string, delay?: number, radius?: number, offset?: number }} opts
 * @returns {{ start: () => void, stop: () => void }} packet controls
 */
function addDataLink(svg, points, { prefix, delay = 0, radius = 12, offset = 0 }) {
  const id = `c-ms-link-${++linkCount}`;
  const a = points[0];
  const b = points[points.length - 1];
  const g = document.createElementNS(SVG_NS, 'g');
  g.innerHTML = `<defs><linearGradient id="${id}-g" gradientUnits="userSpaceOnUse" x1="${a[0]}" y1="${a[1]}" x2="${b[0] + 0.01}" y2="${b[1] + 0.01}"><stop stop-color="#fff" stop-opacity=".95"/><stop offset="1" stop-color="#fff" stop-opacity=".35"/></linearGradient></defs>
    <path id="${id}" class="${prefix}__conn" d="${elbowPath(points, radius)}" stroke="url(#${id}-g)" style="--d:${delay}ms;filter:drop-shadow(0 0 2px rgba(255,255,255,.55))"/>
    <g class="${prefix}__port" style="--d:${delay + 800}ms"><circle class="${prefix}__port-ring" cx="${b[0]}" cy="${b[1]}" r="5"/><circle class="${prefix}__port-core" cx="${b[0]}" cy="${b[1]}" r="2"/></g>
    <circle r="2.8" fill="#fff" opacity="0" style="filter:drop-shadow(0 0 3px #fff)"><animateMotion begin="indefinite" dur="2.6s" repeatCount="indefinite" calcMode="linear" keyPoints="0;1;1" keyTimes="0;.5;1"><mpath href="#${id}"/></animateMotion><animate attributeName="opacity" begin="indefinite" dur="2.6s" repeatCount="indefinite" values="0;1;1;0;0" keyTimes="0;.06;.46;.52;1"/></circle>`;
  svg.appendChild(g);
  const path = g.querySelector('path');
  path.style.setProperty('--len', `${path.getTotalLength()}px`);
  const anims = [...g.querySelectorAll('animateMotion, animate')];
  let timer = 0;
  return {
    start() {
      clearTimeout(timer);
      timer = setTimeout(() => anims.forEach((el) => { try { el.beginElement(); } catch {} }), offset);
    },
    stop() {
      clearTimeout(timer);
      anims.forEach((el) => { try { el.endElement(); } catch {} });
    },
  };
}

/** Box of el relative to ref, from layout rects (ignores nothing: callers pick transform-safe points). */
function relRect(el, ref) {
  const r = el.getBoundingClientRect();
  const o = ref.getBoundingClientRect();
  return { x: r.left - o.left, y: r.top - o.top, w: r.width, h: r.height, cx: r.left - o.left + r.width / 2, cy: r.top - o.top + r.height / 2 };
}

/** Layout box (offsets) of el relative to ref, unaffected by transforms/translate on el. */
function offsetRect(el, ref) {
  let x = 0;
  let y = 0;
  let node = el;
  while (node && node !== ref) {
    x += node.offsetLeft;
    y += node.offsetTop;
    node = node.offsetParent;
  }
  return { x, y, w: el.offsetWidth, h: el.offsetHeight };
}

const isShown = (el) => !!el && el.offsetParent !== null;

function countUp(el, to, duration) {
  const t0 = performance.now();
  let raf = 0;
  const step = (now) => {
    const k = Math.min(1, (now - t0) / duration);
    el.textContent = String(Math.round(to * easeOut(k)));
    if (k < 1) raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);
  return () => cancelAnimationFrame(raf);
}

/** Plays the visible muted loops while root is on screen; pauses them otherwise. */
function autoplayVideos(root, enabled) {
  const videos = [...root.querySelectorAll('video')];
  if (!videos.length) return () => {};
  const pauseAll = () => videos.forEach((v) => v.pause());
  if (!enabled) return pauseAll;
  let onScreen = false;
  const sync = () => {
    videos.forEach((v) => {
      if (onScreen && !document.hidden && isShown(v)) {
        v.muted = true;
        if (v.preload !== 'auto') v.preload = 'auto';
        v.play()?.catch(() => {});
      } else {
        v.pause();
      }
    });
  };
  const io = new IntersectionObserver((entries) => {
    onScreen = entries.some((e) => e.isIntersecting);
    sync();
  });
  io.observe(root);
  const mq = window.matchMedia('(min-width: 990px)');
  mq.addEventListener('change', sync);
  document.addEventListener('visibilitychange', sync);
  return () => {
    io.disconnect();
    mq.removeEventListener('change', sync);
    document.removeEventListener('visibilitychange', sync);
    pauseAll();
  };
}

/** Runs fn once root is at least `threshold` on screen. */
function onceInView(root, fn, threshold = 0.15) {
  const io = new IntersectionObserver((entries) => {
    if (!entries.some((e) => e.isIntersecting)) return;
    io.disconnect();
    fn();
  }, { threshold });
  io.observe(root);
  return () => io.disconnect();
}

// ---- c-ms-device-hero: photos/videos wipe in, cuff tags light up, data links draw from the
// cuff markers into the Muscle Score overlay, the overlay builds and its numbers count up.
// Timings follow the "Muscle Score Hero Motion" prototype.
const deviceHero = defineSection((root) => {
  const stage = root.querySelector('[data-c-ms-stage]');
  const svg = root.querySelector('[data-c-ms-links]');
  const module = root.querySelector('[data-c-ms-module]');
  if (!stage || !svg || !module) return;

  const reduced = prefersReducedMotion();
  const replay = !reduced && !root.dataset.cMsPlayed;
  root.dataset.cMsStarted = '1';
  const timers = [];
  const later = (ms, fn) => timers.push(setTimeout(fn, ms));
  const stops = [];
  let links = [];
  let live = false;

  const markerPoint = (name) => {
    const m = root.querySelector(`[data-c-ms-marker="${name}"]`);
    if (!isShown(m)) return null;
    // The marker scales from its centre while it pops in, so only its centre is measured.
    const r = relRect(m, stage);
    return { x: r.cx, y: r.cy, rad: m.offsetWidth / 2 };
  };

  // Vertical drop from a marker into the overlay's bottom edge, with an elbow when they don't line up.
  const riseTo = (p, mod, inset) => {
    const from = [p.x, p.y - p.rad - 4];
    const bottom = mod.y + mod.h + 4;
    const x = Math.min(Math.max(p.x, mod.x + inset), mod.x + mod.w - inset);
    if (Math.abs(x - p.x) < 1) return [from, [p.x, bottom]];
    const midY = (bottom + from[1]) / 2;
    return [from, [p.x, midY], [x, midY], [x, bottom]];
  };

  const draw = () => {
    links.forEach((l) => l.stop());
    links = [];
    svg.replaceChildren();
    const w = stage.clientWidth;
    const h = stage.clientHeight;
    if (!w || !h) return;
    svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
    const mod = offsetRect(module, stage);
    const prefix = 'c-ms-dh';

    const main = markerPoint('main');
    const detail = markerPoint('detail');
    const mobile = markerPoint('mobile');
    if (main) {
      // Right out of the main marker, up the gap between the two boxes, into the overlay's side.
      const mainBox = offsetRect(root.querySelector('[data-c-ms-box="main"]'), stage);
      const detailBox = offsetRect(root.querySelector('[data-c-ms-box="detail"]'), stage);
      const gapX = (mainBox.x + mainBox.w + detailBox.x) / 2;
      const midY = mod.y + mod.h / 2;
      links.push(addDataLink(svg, [[main.x + main.rad + 4, main.y], [gapX, main.y], [gapX, midY], [mod.x - 4, midY]], { prefix, delay: 1150, radius: 14 }));
    }
    if (detail) links.push(addDataLink(svg, riseTo(detail, mod, 24), { prefix, delay: 1400, offset: 1300 }));
    if (mobile) links.push(addDataLink(svg, riseTo(mobile, mod, mod.w / 2), { prefix, delay: 1150, radius: 10 }));
    if (live) links.forEach((l) => l.start());
  };

  let frame = 0;
  const queueDraw = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(draw);
  };
  const ro = new ResizeObserver(queueDraw);
  ro.observe(stage);
  ro.observe(module);
  draw();
  document.fonts?.ready.then(queueDraw);

  const goLive = () => {
    live = true;
    root.classList.add('is-live');
    links.forEach((l) => l.start());
  };

  stops.push(autoplayVideos(root, !reduced));

  if (!replay) {
    root.classList.remove('is-pre');
    root.classList.add('is-play');
    if (!reduced) goLive();
  } else {
    const nums = [...root.querySelectorAll('[data-c-ms-count]')];
    nums.forEach((n) => (n.textContent = '0'));
    stops.push(onceInView(root, () => {
      root.dataset.cMsPlayed = '1';
      requestAnimationFrame(() => requestAnimationFrame(() => {
        root.classList.remove('is-pre');
        root.classList.add('is-play');
        later(1650, () => nums.forEach((n) => stops.push(countUp(n, Number(n.dataset.cMsCount) || 0, 1200))));
        later(2700, goLive);
      }));
    }));
  }

  return () => {
    timers.forEach(clearTimeout);
    cancelAnimationFrame(frame);
    ro.disconnect();
    links.forEach((l) => l.stop());
    stops.forEach((fn) => fn());
    root.querySelectorAll('[data-c-ms-count]').forEach((n) => (n.textContent = n.dataset.cMsCount));
  };
});

// ---- c-ms-how-scores: the copy and steps build in, the app card opens from its ring, then the
// rings refill on a loop (numbers count, the grade steps through the bands, a head rides each arc).
// Desktop also runs a pressure + movement signal into the card and a data link from step 3.
const HS_FILL = 2000;
const HS_DRAIN = 650;

const howScores = defineSection((root, settings) => {
  const card = root.querySelector('[data-c-ms-card]');
  const inner = root.querySelector('[data-c-ms-inner]');
  const svg = root.querySelector('.c-ms-hs__links');
  const signal = root.querySelector('[data-c-ms-signal]');
  if (!card || !inner) return;

  const reduced = prefersReducedMotion();
  const replay = !reduced && !root.dataset.cMsPlayed;
  root.dataset.cMsStarted = '1';
  const bands = settings.bands || ['Poor', 'Average', 'Good', 'Excellent'];
  const colors = settings.colors || ['#CD504D', '#F4B14F', '#22CDA4', '#69BFFF'];
  const LOOP = Math.max(4000, Number(settings.loop) || 9500);
  const band = (v) => (v < 40 ? 0 : v < 60 ? 1 : v < 80 ? 2 : 3);

  const ring = card.querySelector('.c-ms-ring__svg');
  const centre = ring ? ring.viewBox.baseVal.width / 2 : 0;
  const sides = [0, 1].map((i) => {
    const arc = card.querySelector(`[data-c-ms-arc="${i}"]`);
    return arc && {
      arc,
      C: Number(arc.dataset.c),
      r: Number(arc.dataset.r),
      v: Number(arc.dataset.v),
      tip: card.querySelector(`[data-c-ms-tip="${i}"]`),
      value: card.querySelector(`[data-c-ms-value="${i}"]`),
      grade: card.querySelector(`[data-c-ms-grade="${i}"]`),
      band: -1,
    };
  }).filter(Boolean);

  const drawCard = (k) => {
    sides.forEach((s) => {
      const v = s.v * k;
      s.arc.style.strokeDashoffset = String(s.C * (1 - v / 100));
      s.arc.style.opacity = v < 0.5 ? '0' : '1';
      if (s.tip) {
        const a = (2 * Math.PI * v) / 100 - Math.PI / 2;
        s.tip.setAttribute('cx', String(centre + s.r * Math.cos(a)));
        s.tip.setAttribute('cy', String(centre + s.r * Math.sin(a)));
        s.tip.style.opacity = v < 1 ? '0' : '1';
      }
      if (s.value) s.value.textContent = String(Math.round(v));
      const b = band(Math.round(v));
      if (s.grade && b !== s.band) {
        const first = s.band === -1;
        s.band = b;
        s.grade.textContent = bands[b];
        s.grade.style.color = colors[b];
        if (!first && !reduced) {
          s.grade.animate([{ opacity: 0.2, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }], { duration: 260, easing: 'ease-out' });
        }
      }
    });
  };

  // Ring loop: runs only while the card is on screen and the tab is visible.
  let raf = 0;
  let loopStart = 0;
  let looping = false;
  let onScreen = false;
  const tick = (now) => {
    const t = (now - loopStart) % LOOP;
    const k = t < HS_FILL ? easeOut(t / HS_FILL) : t < LOOP - HS_DRAIN ? 1 : 1 - easeInOut((t - (LOOP - HS_DRAIN)) / HS_DRAIN);
    drawCard(k);
    raf = requestAnimationFrame(tick);
  };
  const syncLoop = () => {
    cancelAnimationFrame(raf);
    raf = 0;
    if (looping && onScreen && !document.hidden) raf = requestAnimationFrame(tick);
  };
  const viewIO = new IntersectionObserver((entries) => {
    onScreen = entries.some((e) => e.isIntersecting);
    syncLoop();
    if (signalAnim) onScreen ? signalAnim.play() : signalAnim.pause();
  });
  viewIO.observe(card);
  document.addEventListener('visibilitychange', syncLoop);
  const startLoop = () => {
    loopStart = performance.now();
    looping = true;
    syncLoop();
  };

  // Signal: two sine traces (pressure in red, movement in white) scrolling into the card.
  let signalAnim = null;
  const buildSignal = () => {
    if (!signal) return;
    signalAnim?.cancel();
    signalAnim = null;
    signal.replaceChildren();
    const w = signal.clientWidth;
    const h = signal.clientHeight;
    if (!w || !h) return;
    const k = h / 120;
    const period = 150 * k;
    const total = w + period * 2;
    const wave = (amp, phase) => {
      let d = '';
      for (let x = 0; x <= total; x += 2) d += `${x ? 'L' : 'M'}${x} ${(h / 2 + amp * Math.sin((x / period) * 2 * Math.PI + phase)).toFixed(2)}`;
      return d;
    };
    const track = document.createElementNS(SVG_NS, 'svg');
    track.setAttribute('width', String(total));
    track.setAttribute('height', String(h));
    track.setAttribute('viewBox', `0 0 ${total} ${h}`);
    track.setAttribute('fill', 'none');
    track.style.filter = 'drop-shadow(0 0 6px rgba(225,0,23,.35))';
    track.innerHTML = `<path d="${wave(30 * k, Math.PI)}" stroke="#E10017" stroke-width="2"/><path d="${wave(14 * k, 0)}" stroke="#fff" stroke-opacity=".7" stroke-width="1.5"/>`;
    signal.appendChild(track);
    if (!reduced) {
      signalAnim = track.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${-period}px)` }], { duration: 2600, iterations: Infinity });
      if (!onScreen) signalAnim.pause();
    }
  };

  // Data link from the last step's icon into the card's right edge (desktop only).
  let links = [];
  let live = false;
  const drawLinks = () => {
    links.forEach((l) => l.stop());
    links = [];
    if (!svg) return;
    svg.replaceChildren();
    const icons = root.querySelectorAll('[data-c-ms-step-icon]');
    const icon = icons[icons.length - 1];
    if (!isShown(svg) || !icon) return;
    svg.setAttribute('viewBox', `0 0 ${inner.clientWidth} ${inner.clientHeight}`);
    const c = relRect(card, inner);
    const i = relRect(icon, inner);
    if (i.cy < c.y || i.cy > c.y + c.h || i.x <= c.x + c.w) return;
    links.push(addDataLink(svg, [[i.cx - icon.offsetWidth / 2 - 6, i.cy], [c.x + c.w + 4, i.cy]], { prefix: 'c-ms-hs', delay: 2300 }));
    if (live) links.forEach((l) => l.start());
  };

  const layout = () => {
    buildSignal();
    drawLinks();
    if (ring) {
      // The card background opens from the ring's centre.
      const r = relRect(ring, card);
      card.style.setProperty('--rx', `${r.cx}px`);
      card.style.setProperty('--ry', `${r.cy}px`);
    }
  };
  let frame = 0;
  const queueLayout = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(layout);
  };
  const ro = new ResizeObserver(queueLayout);
  ro.observe(inner);
  layout();
  document.fonts?.ready.then(queueLayout);

  const timers = [];
  const later = (ms, fn) => timers.push(setTimeout(fn, ms));
  const goLive = () => {
    live = true;
    root.classList.add('is-live');
    card.classList.add('is-card-live');
    links.forEach((l) => l.start());
  };
  const burst = () => {
    if (!ring) return;
    const r = relRect(ring, card);
    const el = document.createElement('div');
    el.className = 'c-ms-hs__burst';
    el.style.left = `${r.cx}px`;
    el.style.top = `${r.cy}px`;
    card.appendChild(el);
    const anim = el.animate(
      [{ opacity: 0, transform: 'scale(.2)' }, { opacity: 1, transform: 'scale(1)', offset: 0.3 }, { opacity: 0, transform: 'scale(1.9)' }],
      { duration: 900, easing: 'ease-out' }
    );
    anim.onfinish = () => el.remove();
  };

  let stopInView = () => {};
  if (reduced) {
    drawCard(1);
  } else if (!replay) {
    root.classList.remove('is-pre');
    root.classList.add('is-play');
    card.classList.remove('is-card-pre');
    card.classList.add('is-card-play');
    goLive();
    startLoop();
  } else {
    drawCard(0);
    stopInView = onceInView(root, () => {
      root.dataset.cMsPlayed = '1';
      requestAnimationFrame(() => requestAnimationFrame(() => {
        root.classList.remove('is-pre');
        root.classList.add('is-play');
        later(1300, () => {
          card.classList.remove('is-card-pre');
          card.classList.add('is-card-play');
        });
        later(1850, () => {
          burst();
          startLoop();
        });
        later(3400, goLive);
      }));
    }, 0.3);
  }

  return () => {
    stopInView();
    timers.forEach(clearTimeout);
    cancelAnimationFrame(frame);
    cancelAnimationFrame(raf);
    looping = false;
    viewIO.disconnect();
    ro.disconnect();
    document.removeEventListener('visibilitychange', syncLoop);
    links.forEach((l) => l.stop());
    signalAnim?.cancel();
  };
});

// ---- c-ms-teaser (PDP): the copy builds in, the locked card opens from its ring, the ring fills,
// the values count 100 → 0 blurred while the ring drains, then resolve to sharp "??" and the locks
// shake. Timings follow the "Muscle Score PDP Teaser" prototype (ms from the start of the play).
const MT = { burst: 500, fill: 650, count: 1500, countDur: 1800 };

const teaser = defineSection((root) => {
  const card = root.querySelector('[data-c-ms-card]');
  if (!card) return;
  const reduced = prefersReducedMotion();
  const replay = !reduced && !root.dataset.cMsPlayed;
  root.dataset.cMsStarted = '1';

  const vals = [...card.querySelectorAll('[data-c-ms-val]')];
  const locked = vals.map((v) => v.textContent);
  const arcs = [...card.querySelectorAll('[data-c-ms-arc]')].map((el) => ({ el, C: Number(el.dataset.c) }));
  const ring = card.querySelector('[data-c-ms-ring]');

  const setRing = (p, animate) => {
    arcs.forEach(({ el, C }) => {
      el.style.transition = animate ? 'stroke-dashoffset .6s cubic-bezier(.2,.7,.1,1)' : 'none';
      el.style.strokeDashoffset = String(C * (1 - p));
      el.style.opacity = p > 0.002 ? '1' : '0';
    });
  };
  const setCount = (score, delta) => {
    vals.forEach((v) => {
      v.textContent = v.dataset.cMsVal === 'delta' ? delta : score;
    });
  };
  const ringCentre = () => {
    const r = relRect(ring, card);
    return { x: r.cx, y: r.cy };
  };

  const timers = [];
  const later = (ms, fn) => timers.push(setTimeout(fn, ms));
  let raf = 0;
  let stopInView = () => {};

  const rest = () => {
    root.classList.remove('is-pre');
    root.classList.add('is-play', 'is-rest');
    card.classList.remove('is-blurred');
    vals.forEach((v, i) => {
      v.classList.remove('is-out', 'is-qin');
      v.textContent = locked[i];
    });
    setRing(0, false);
  };

  const play = () => {
    root.dataset.cMsPlayed = '1';
    const c = ringCentre();
    card.style.setProperty('--rx', `${c.x}px`);
    card.style.setProperty('--ry', `${c.y}px`);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      root.classList.remove('is-pre');
      root.classList.add('is-play');
      later(MT.burst, () => {
        const p = ringCentre();
        const el = document.createElement('div');
        el.className = 'c-ms-mt__burst';
        el.style.left = `${p.x}px`;
        el.style.top = `${p.y}px`;
        card.appendChild(el);
        el.animate(
          [{ opacity: 0, transform: 'scale(.2)' }, { opacity: 1, transform: 'scale(1)', offset: 0.3 }, { opacity: 0, transform: 'scale(1.9)' }],
          { duration: 900, easing: 'ease-out' }
        ).onfinish = () => el.remove();
      });
      later(MT.fill, () => setRing(1, true));
      later(MT.count, () => {
        const t0 = performance.now();
        const step = (now) => {
          const k = Math.min(1, (now - t0) / MT.countDur);
          const e = easeInOut(k);
          const score = Math.round(100 * (1 - e));
          const delta = Math.round(30 * (1 - e));
          setCount(String(score), delta > 0 ? `+${delta}` : '0');
          setRing(1 - e, false);
          if (k < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
      });
      const end = MT.count + MT.countDur;
      later(end, () => vals.forEach((v) => v.classList.add('is-out')));
      vals.forEach((v, i) => later(end + 200 + i * 90, () => {
        v.classList.remove('is-out');
        card.classList.remove('is-blurred');
        v.textContent = locked[i];
        v.classList.add('is-qin');
        requestAnimationFrame(() => requestAnimationFrame(() => v.classList.remove('is-qin')));
      }));
      later(end + 520, () => root.classList.add('is-rest', 'is-shake'));
      later(end + 1000, () => root.classList.remove('is-shake'));
    }));
  };

  if (!replay) {
    rest();
  } else {
    card.classList.add('is-blurred');
    setCount('100', '+30');
    setRing(0, false);
    stopInView = onceInView(root, play, 0.4);
  }

  return () => {
    stopInView();
    timers.forEach(clearTimeout);
    cancelAnimationFrame(raf);
    // A rebuild (resize) re-inits straight into the rest state once the band has played.
    if (root.dataset.cMsPlayed) {
      rest();
      root.classList.remove('is-shake');
    }
  };
});

// ---------------------------------------------------------------------------
// 9. Boot + theme editor events
// ---------------------------------------------------------------------------

const registry = {
  'c-ms-hero': hero,
  'c-ms-missing-metric': missingMetric,
  'c-ms-how-it-works': howItWorks,
  'c-ms-signal': signal,
  'c-ms-more-than-reps': moreThanReps,
  'c-ms-score': score,
  'c-ms-trend': trend,
  'c-ms-device-hero': deviceHero,
  'c-ms-how-scores': howScores,
  'c-ms-teaser': teaser,
};

const sectionRoots = (scope) =>
  scope.matches?.('[data-c-ms-section]') ? [scope] : scope.querySelectorAll('[data-c-ms-section]');

function initWithin(scope) {
  sectionRoots(scope).forEach((root) => {
    const mod = registry[root.dataset.cMsSection];
    if (!mod) return;
    try {
      mod.init(root);
    } catch (err) {
      console.error(`[ms] ${root.dataset.cMsSection} failed to init`, err);
    }
  });
}

function destroyWithin(scope) {
  sectionRoots(scope).forEach((root) => registry[root.dataset.cMsSection]?.destroy(root));
}

// Register GSAP (and the refresh fix above) before any theme refresh can run.
getGsap();

// The heroes initialize right away. Everything below the fold waits for load +
// idle, so pin setup and scroll measurement never compete with first paint.
document
  .querySelectorAll('[data-c-ms-section="c-ms-hero"], [data-c-ms-section="c-ms-device-hero"]')
  .forEach((heroRoot) => initWithin(heroRoot));
const initRest = () => {
  initWithin(document);
  window.ScrollTrigger?.refresh();
};
const idle = () =>
  window.requestIdleCallback ? requestIdleCallback(initRest, { timeout: 1500 }) : setTimeout(initRest, 300);
if (document.readyState === 'complete') idle();
else window.addEventListener('load', idle, { once: true });

// Pins and scrubbed scenes measure layout, so re-measure once fonts settle.
document.fonts?.ready.then(() => window.ScrollTrigger?.refresh());

// Content above a pin can also change height after it was measured (late images, app widgets,
// other sections laying out on load). Re-measure when the page height changes on its own;
// changes made by a refresh itself (pin spacers) are ignored.
if (window.ScrollTrigger && 'ResizeObserver' in window) {
  let settledHeight = 0;
  let heightTimer = 0;
  window.ScrollTrigger.addEventListener('refresh', () => (settledHeight = document.documentElement.scrollHeight));
  new ResizeObserver(() => {
    if (window.ScrollTrigger.isRefreshing) return;
    if (Math.abs(document.documentElement.scrollHeight - settledHeight) < 2) return;
    clearTimeout(heightTimer);
    heightTimer = setTimeout(() => window.ScrollTrigger.refresh(), 250);
  }).observe(document.body);
}

document.addEventListener('shopify:section:load', (e) => {
  initWithin(e.target);
  window.ScrollTrigger?.refresh();
});
document.addEventListener('shopify:section:unload', (e) => {
  destroyWithin(e.target);
  window.ScrollTrigger?.refresh();
});
document.addEventListener('shopify:section:reorder', () => window.ScrollTrigger?.refresh());

// ---- Keep the reader in place across breakpoint changes.
// A breakpoint change can switch sections between pinned and static layouts, which
// changes the page height a lot. The reader's place (the top-most c-ms section on
// screen and how far into it) is tracked while scrolling, so it's still measured in
// the old layout when the resize arrives, and restored after the rebuild.

let anchor = null;
let resizing = false;

function readAnchor() {
  const offset = headerOffset();
  const root = [...document.querySelectorAll('[data-c-ms-section]')].find((r) => r.getBoundingClientRect().bottom > offset);
  if (!root) return null;
  const rect = root.getBoundingClientRect();
  return { root, ratio: rect.height ? (offset - rect.top) / rect.height : 0 };
}

function restoreAnchor(target) {
  if (!target?.root.isConnected) return;
  const offset = headerOffset();
  const rect = target.root.getBoundingClientRect();
  // Same share of the way through the section, but never past the point where it starts
  // leaving the screen (a pinned section is much taller than its static layout).
  const into = Math.min(target.ratio * rect.height, Math.max(0, rect.height - (window.innerHeight - offset)));
  jumpTo(window.scrollY + rect.top - offset + into);
}

let anchorQueued = false;
window.addEventListener('scroll', () => {
  if (anchorQueued) return;
  anchorQueued = true;
  requestAnimationFrame(() => {
    anchorQueued = false;
    if (!resizing) anchor = readAnchor();
  });
}, { passive: true });

const rebuild = () => {
  const target = anchor ?? readAnchor();
  destroyWithin(document);
  initWithin(document);
  window.ScrollTrigger?.refresh();
  restoreAnchor(target);
  window.ScrollTrigger?.update();
  anchor = readAnchor();
};

let resizeTimer;
let previousWidth = window.innerWidth;
let previousHeight = window.innerHeight;
window.addEventListener('resize', () => {
  resizing = true;
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    // Ignore mobile address-bar movement; rebuild for a changed layout.
    const changed = Math.abs(previousWidth - window.innerWidth) >= 40 || Math.abs(previousHeight - window.innerHeight) >= 140;
    if (changed) {
      previousWidth = window.innerWidth;
      previousHeight = window.innerHeight;
      rebuild();
    }
    resizing = false;
  }, 200);
}, { passive: true });
window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', rebuild);
